import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
    checkNovaPoshtaLegalEntityWms,
    createNovaPoshtaSenderPoint,
    discoverNovaPoshtaLegalEntityRefs,
    getNovaPoshtaGlobalSettings,
    getNovaPoshtaLegalEntities,
    importNovaPoshtaEnvSettings,
    updateNovaPoshtaGlobalSettings,
    updateNovaPoshtaLegalEntitySettings,
    updateNovaPoshtaSenderPoint,
} from "../api/novaPoshtaApi";
import {
    getCheckboxGlobalSettings,
    importCheckboxEnvSettings,
    updateCheckboxGlobalSettings,
    forceCreateCheckboxReceipt,
} from "../api/checkboxApi";
import { getBusinessOrganizations } from "../api/businessOrganizationsApi";
import {
    createPromAccount,
    downloadPromSkippedReport,
    generatePromFeed,
    getPromAccounts,
    getPromCategoryMappings,
    getPromInternalCategories,
    getPromJobs,
    getPromRegions,
    getPromSupplierRegions,
    getPromPlatformCategories,
    getPromSupplierSettings,
    markPromPlatformCategoryQuick,
    syncPromPlatformCategories,
    syncPromProductLinks,
    syncPromStockPrices,
    unmarkPromPlatformCategoryQuick,
    updatePromAccount,
    updatePromSupplierRegions,
    upsertPromCategoryMapping,
    uploadPromPlatformCategories,
    uploadPromProductScope,
    clearPromProductScope,
} from "../api/promApi";
import PromMappingsPage from "./PromMappingsPage";
import PromAttributesPage from "./PromAttributesPage";
import PromTopBar from "../components/marketplace/PromTopBar";
import MarketplacePricingTab from "../components/marketplace/pricing/MarketplacePricingTab";
import MarketplaceSendersTable from "../components/marketplace/senders/MarketplaceSendersTable";
import useMarketplacePricingMode from "../components/marketplace/pricing/useMarketplacePricingMode";
import TabletkiIntegrationPage from "./TabletkiIntegrationPage";
import MarketplaceOrdersPanel from "../components/MarketplaceOrdersPanel";
import RozetkaIntegrationPage from "./RozetkaIntegrationPage";
import RozetkaMappingPage from "./RozetkaMappingPage";
import KastaIntegrationPage from "./KastaIntegrationPage";
import TurboSmsIntegrationPage from "./TurboSmsIntegrationPage";
import "./IntegrationsPage.css";

const pageStyle = {
    padding: "24px",
    display: "grid",
    gap: "18px",
    maxWidth: "1440px",
    margin: "0 auto",
    boxSizing: "border-box",
};

const panelStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #d9dee8",
    borderRadius: "8px",
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
};

const panelBodyStyle = {
    padding: "20px 24px",
};

const titleStyle = {
    margin: 0,
    fontSize: "22px",
    fontWeight: 800,
    color: "#111827",
};

const sectionTitleStyle = {
    margin: 0,
    fontSize: "18px",
    fontWeight: 800,
    color: "#111827",
};

const mutedTextStyle = {
    margin: 0,
    color: "#64748b",
    fontSize: "13px",
};

const hintTextStyle = {
    margin: 0,
    color: "#64748b",
    fontSize: "12px",
    lineHeight: 1.35,
    fontWeight: 500,
};

const warningBoxStyle = {
    border: "1px solid #f59e0b",
    borderRadius: "8px",
    backgroundColor: "#fffbeb",
    color: "#92400e",
    padding: "10px 12px",
    fontSize: "13px",
    lineHeight: 1.45,
};

const infoBoxStyle = {
    border: "1px solid #bfdbfe",
    borderRadius: "8px",
    backgroundColor: "#eff6ff",
    color: "#1e3a8a",
    padding: "10px 12px",
    fontSize: "13px",
    lineHeight: 1.45,
};

const buttonStyle = {
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "9px 12px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    backgroundColor: "#ffffff",
    color: "#111827",
};

const primaryButtonStyle = {
    ...buttonStyle,
    borderColor: "#2563eb",
    backgroundColor: "#2563eb",
    color: "#ffffff",
};

const inputStyle = {
    width: "100%",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    padding: "0 12px",
    fontSize: "14px",
    lineHeight: "20px",
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    height: "44px",
    minHeight: "44px",
};

const labelStyle = {
    display: "grid",
    gap: "6px",
    color: "#111827",
    fontSize: "13px",
    fontWeight: 700,
};

const fieldContainerStyle = {
    ...labelStyle,
    gridTemplateRows: "auto 44px auto",
    alignItems: "start",
    minWidth: 0,
};

const fieldLabelTextStyle = {
    display: "flex",
    alignItems: "flex-end",
    minHeight: "22px",
    lineHeight: 1.2,
};

const fieldHintStyle = {
    ...hintTextStyle,
    minHeight: 0,
};

const gridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: "16px 18px",
    alignItems: "start",
};

const gridFourStyle = {
    ...gridStyle,
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
};

const emptySettings = {
    api_key: "",
    fulfillment_api_key: "",
    sender_ref: "",
    contact_sender_ref: "",
    city_sender_ref: "",
    sender_phone: "",
    default_description: "",
    is_active: true,
    is_default: false,
    wms_enabled: false,
    wms_auth_mode: "basic",
    wms_organization: "",
    wms_ttn_organization: "",
    wms_login: "",
    wms_password: "",
    wms_access_key: "",
    wms_default_dest_warehouse: "",
};

const emptyPoint = {
    id: null,
    point_name: "",
    sender_address_ref: "",
    city_sender_ref: "",
    sender_ref: "",
    contact_sender_ref: "",
    point_type: "standard",
    wms_dest_warehouse: "",
    is_default: false,
    is_fulfillment_default: false,
    is_active: true,
    notes: "",
};

const nullable = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    const normalized = String(value).trim();
    return normalized || null;
};

const normalizePayload = (payload) => {
    const next = {};
    Object.entries(payload).forEach(([key, value]) => {
        next[key] = typeof value === "boolean" || typeof value === "number" ? value : nullable(value);
    });
    return next;
};

const wmsValueFields = [
    "fulfillment_api_key",
    "wms_organization",
    "wms_ttn_organization",
    "wms_login",
    "wms_password",
    "wms_access_key",
    "wms_default_dest_warehouse",
];

const clearDisabledWmsFields = (settings, emptyValue = null) => {
    const next = { ...settings };
    if (!next.wms_enabled) {
        wmsValueFields.forEach((field) => {
            next[field] = emptyValue;
        });
        next.wms_auth_mode = "basic";
    }
    return next;
};

const positiveNumberOrDefault = (value, fallback) => {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : fallback;
};

const positiveNumberOrNull = (value) => {
    if (value === null || value === undefined || String(value).trim() === "") {
        return null;
    }
    const numberValue = Number(value);
    return Number.isFinite(numberValue) && numberValue > 0 ? numberValue : null;
};

const formatDateTime = (value) => {
    if (!value) {
        return "нет даты";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString("ru-RU", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
    });
};

const formatJobTime = (job) => {
    if (!job) {
        return "ещё не запускалось";
    }
    const finished = job.finished_at || job.created_at;
    const started = job.started_at;
    if (started && finished && started !== finished) {
        return `${formatDateTime(started)} → ${formatDateTime(finished)}`;
    }
    return formatDateTime(finished || started);
};

const statusLabel = (statusValue) => {
    const labels = {
        success: "OK",
        partial: "частично",
        failed: "ошибка",
        running: "выполняется",
        skipped: "пропущено",
        queued: "в очереди",
    };
    return labels[statusValue] || statusValue || "unknown";
};

const jobTypeLabel = (jobType) => {
    const labels = {
        feed_generate: "Feed контента",
        prom_product_links_sync: "Связки товаров Prom",
        prom_stock_price_sync: "API цены/остатки",
    };
    return labels[jobType] || jobType || "job";
};

const formatApiError = (error, fallback) => {
    const detail = error?.response?.data?.detail;
    if (Array.isArray(detail)) {
        return detail
            .map((item) => {
                if (!item || typeof item !== "object") {
                    return String(item);
                }
                const location = Array.isArray(item.loc) ? item.loc.join(".") : item.loc;
                const message = item.msg || JSON.stringify(item);
                return location ? `${location}: ${message}` : message;
            })
            .join("; ");
    }
    if (detail && typeof detail === "object") {
        return detail.msg || JSON.stringify(detail);
    }
    if (error?.message === "Network Error") {
        return "Network Error: backend API недоступен или ответ заблокирован CORS. Перезапустите FastAPI на 127.0.0.1:8000 после изменений.";
    }
    return detail || error?.message || fallback;
};

const HelpTip = ({ title, items, align }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, [open]);

    return (
        <div className={`ip-help${open ? " open" : ""}`} ref={wrapRef}>
            <button type="button" className="ip-help-btn" aria-label="Справка" onClick={() => setOpen((value) => !value)}>?</button>
            {open && (
                <div className="ip-help-pop" style={align === "left" ? { right: "auto", left: 0 } : undefined}>
                    <div className="ip-help-pop-head">
                        <strong>{title}</strong>
                        <button type="button" className="ip-help-close" aria-label="Закрыть" onClick={() => setOpen(false)}>×</button>
                    </div>
                    <dl>
                        {items.map(([term, text]) => (
                            <React.Fragment key={term}>
                                <dt>{term}</dt>
                                <dd>{text}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                </div>
            )}
        </div>
    );
};

const formatOrgTitle = (org) => {
    if (!org) {
        return "Юрлицо";
    }
    const tax = org.tax_id ? ` / ${org.tax_id}` : "";
    return `${org.short_name || `ID ${org.id}`}${tax}`;
};

const firstItem = (items) => (Array.isArray(items) && items.length ? items[0] : null);

const isEmptyRef = (value) => {
    const normalized = String(value || "").trim();
    return !normalized || normalized === "00000000-0000-0000-0000-000000000000";
};

const firstValidRef = (...values) => {
    const value = values.find((item) => !isEmptyRef(item));
    return value ? String(value).trim() : null;
};

const buildNpRefsDraft = (discovery) => {
    const point = firstItem(discovery?.sender_points);
    const sender = point ? null : firstItem(discovery?.senders);
    const senderRef = point?.sender_ref || sender?.ref || null;
    const contact = point ? null : firstItem(senderRef ? discovery?.contacts_by_sender?.[senderRef] : null);
    const address = point ? null : firstItem(senderRef ? discovery?.addresses_by_sender?.[senderRef] : null);
    return {
        sender_ref: firstValidRef(senderRef),
        contact_sender_ref: firstValidRef(point?.contact_sender_ref, contact?.ref),
        city_sender_ref: firstValidRef(point?.city_sender_ref, address?.city_ref, sender?.city_ref),
        sender_phone: point?.sender_phone || contact?.phones || null,
        sender_address_ref: firstValidRef(point?.sender_address_ref, address?.ref),
        point_name: point?.point_name || address?.description || "Основная точка отправки",
    };
};

const buildPointDraftFromDiscovery = (point, current = emptyPoint) => ({
    ...current,
    point_name: point.point_name || current.point_name || "Основная точка отправки",
    sender_address_ref: point.sender_address_ref || current.sender_address_ref,
    city_sender_ref: point.city_sender_ref || current.city_sender_ref,
    sender_ref: point.sender_ref || current.sender_ref,
    contact_sender_ref: point.contact_sender_ref || current.contact_sender_ref,
    point_type: "standard",
    is_default: true,
    is_fulfillment_default: false,
    is_active: true,
});

const senderAddressLooksWrong = (point) => {
    const addressRef = String(point?.sender_address_ref || "").trim();
    if (!addressRef) {
        return false;
    }
    return addressRef === String(point?.sender_ref || "").trim()
        || addressRef === String(point?.contact_sender_ref || "").trim();
};

const NovaPoshtaIntegrationPage = () => {
    const [activeTab, setActiveTab] = useState("global");
    const [globalSettings, setGlobalSettings] = useState(null);
    const [legalEntities, setLegalEntities] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(null);
    const [settingsDraft, setSettingsDraft] = useState(emptySettings);
    const [pointDraft, setPointDraft] = useState(emptyPoint);
    const [status, setStatus] = useState("");
    const [discovery, setDiscovery] = useState(null);
    const [warehouseSearch, setWarehouseSearch] = useState({
        cityName: "Київ",
        query: "",
    });
    const [wmsCheck, setWmsCheck] = useState(null);
    const [showAdvancedWms, setShowAdvancedWms] = useState(false);
    const [showLegalAdvanced, setShowLegalAdvanced] = useState(false);
    const [legalCardTab, setLegalCardTab] = useState("main");

    const loadData = useCallback(async () => {
        const [globalData, entitiesData] = await Promise.all([
            getNovaPoshtaGlobalSettings(),
            getNovaPoshtaLegalEntities(),
        ]);
        setGlobalSettings(globalData);
        setLegalEntities(entitiesData);
        setSelectedOrgId((current) => current || entitiesData[0]?.business_organization?.id || null);
    }, []);

    useEffect(() => {
        loadData().catch((error) => setStatus(formatApiError(error, "Не удалось загрузить настройки НП.")));
    }, [loadData]);

    const selectedView = useMemo(() => {
        return legalEntities.find((item) => item.business_organization?.id === selectedOrgId) || null;
    }, [legalEntities, selectedOrgId]);

    useEffect(() => {
        setSettingsDraft(clearDisabledWmsFields({ ...emptySettings, ...(selectedView?.settings || {}) }, ""));
        setPointDraft(emptyPoint);
        setDiscovery(null);
        setWmsCheck(null);
        setShowLegalAdvanced(false);
        setLegalCardTab("main");
    }, [selectedView]);

    const saveGlobalSettings = async () => {
        try {
            setStatus("Сохранение общих настроек...");
            const payload = {
                ...normalizePayload(globalSettings || {}),
                default_business_organization_id: globalSettings?.default_business_organization_id
                    ? Number(globalSettings.default_business_organization_id)
                    : null,
                default_seats: positiveNumberOrDefault(globalSettings?.default_seats, 1),
                default_weight_kg: positiveNumberOrDefault(globalSettings?.default_weight_kg, 1.6),
                default_volume_m3: positiveNumberOrDefault(globalSettings?.default_volume_m3, 0.0076),
                wms_timeout_seconds: positiveNumberOrDefault(globalSettings?.wms_timeout_seconds, 60),
                wms_default_weight_kg: positiveNumberOrNull(globalSettings?.wms_default_weight_kg),
                wms_default_length_cm: positiveNumberOrNull(globalSettings?.wms_default_length_cm),
                wms_default_height_cm: positiveNumberOrNull(globalSettings?.wms_default_height_cm),
                wms_default_width_cm: positiveNumberOrNull(globalSettings?.wms_default_width_cm),
                wms_fact_poll_interval_seconds: positiveNumberOrNull(globalSettings?.wms_fact_poll_interval_seconds),
                wms_fact_lookback_days: positiveNumberOrNull(globalSettings?.wms_fact_lookback_days),
                wms_fact_max_invoices: positiveNumberOrNull(globalSettings?.wms_fact_max_invoices),
            };
            delete payload.id;
            delete payload.created_at;
            delete payload.updated_at;
            const saved = await updateNovaPoshtaGlobalSettings(payload);
            setGlobalSettings(saved);
            setStatus("Общие настройки сохранены.");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сохранить общие настройки."));
        }
    };

    const saveLegalSettings = async () => {
        if (!selectedOrgId) {
            return;
        }
        try {
            if (senderAddressLooksWrong(pointDraft)) {
                setStatus("Sender address ref должен быть ref адреса/отделения, а не sender/contact ref.");
                return;
            }
            setStatus("Сохранение настроек юрлица и точки отправки...");
            const legalPayload = {
                ...clearDisabledWmsFields(normalizePayload(settingsDraft), null),
                wms_auth_mode: "basic",
                wms_ttn_organization: nullable(settingsDraft.wms_ttn_organization),
            };
            if (!legalPayload.wms_enabled) {
                legalPayload.wms_ttn_organization = null;
            }
            delete legalPayload.id;
            delete legalPayload.business_organization_id;
            delete legalPayload.created_at;
            delete legalPayload.updated_at;
            await updateNovaPoshtaLegalEntitySettings(selectedOrgId, legalPayload);

            const pointPayload = normalizePayload(pointDraft);
            delete pointPayload.id;
            delete pointPayload.legal_entity_settings_id;
            delete pointPayload.created_at;
            delete pointPayload.updated_at;
            if (pointPayload.point_name && pointPayload.sender_address_ref) {
                if (pointDraft.id) {
                    await updateNovaPoshtaSenderPoint(selectedOrgId, pointDraft.id, pointPayload);
                } else {
                    await createNovaPoshtaSenderPoint(selectedOrgId, pointPayload);
                }
            }
            await loadData();
            setStatus("Настройки юрлица сохранены.");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сохранить настройки юрлица."));
        }
    };

    const savePoint = async () => {
        if (!selectedOrgId) {
            return;
        }
        try {
            if (senderAddressLooksWrong(pointDraft)) {
                setStatus("Sender address ref должен быть ref адреса/отделения, а не sender/contact ref.");
                return;
            }
            const payload = normalizePayload(pointDraft);
            delete payload.id;
            setStatus("Сохранение точки отправки...");
            if (pointDraft.id) {
                await updateNovaPoshtaSenderPoint(selectedOrgId, pointDraft.id, payload);
            } else {
                await createNovaPoshtaSenderPoint(selectedOrgId, payload);
            }
            await loadData();
            setPointDraft(emptyPoint);
            setStatus("Точка отправки сохранена.");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сохранить точку отправки."));
        }
    };

    const importEnv = async () => {
        if (!selectedOrgId) {
            return;
        }
        const orgTitle = formatOrgTitle(selectedOrg);
        const confirmed = window.confirm(
            `Импортировать текущие NP_* значения из .env в юрлицо "${orgTitle}"?\n\n` +
            "Это действие не определяет владельца ключей автоматически. Если .env сейчас содержит настройки Петренко, не импортируйте их в Цимко.",
        );
        if (!confirmed) {
            return;
        }
        try {
            setStatus("Импорт текущих env-настроек в выбранное юрлицо...");
            const result = await importNovaPoshtaEnvSettings({
                business_organization_id: selectedOrgId,
                overwrite: false,
            });
            await loadData();
            setStatus(`Импортировано полей: ${result.imported_fields.length}, точек: ${result.sender_points_imported}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось импортировать env-настройки."));
        }
    };

    const runDiscovery = async () => {
        if (!selectedOrgId) {
            return;
        }
        try {
            setStatus("Получение отправителей, контактов и адресов из кабинета НП...");
            setDiscovery(null);
            const warehouseQuery = warehouseSearch.query.trim();
            const result = await discoverNovaPoshtaLegalEntityRefs(selectedOrgId, {
                api_key: settingsDraft.api_key,
                warehouse_city_name: warehouseSearch.cityName,
                warehouse_city_ref: settingsDraft.city_sender_ref,
                warehouse_query: warehouseQuery,
            });
            const refsDraft = buildNpRefsDraft(result);
            setSettingsDraft((current) => ({
                ...current,
                sender_ref: refsDraft.sender_ref || current.sender_ref,
                contact_sender_ref: refsDraft.contact_sender_ref || current.contact_sender_ref,
                city_sender_ref: refsDraft.city_sender_ref || current.city_sender_ref,
                sender_phone: refsDraft.sender_phone || current.sender_phone,
            }));
            if (refsDraft.sender_address_ref) {
                setPointDraft((current) => buildPointDraftFromDiscovery(
                    refsDraft,
                    findExistingSenderPoint(refsDraft) || current,
                ));
            }
            setDiscovery(result);
            const pointsCount = Array.isArray(result.sender_points) ? result.sender_points.length : 0;
            if (pointsCount > 0) {
                setStatus(
                    `Refs найдены. Отправителей: ${result.senders_count || 0}, точек: ${pointsCount}. ` +
                    "Выберите точку ниже или сохраните уже подставленную.",
                );
            } else {
                setStatus(
                    `Refs найдены. Отправителей: ${result.senders_count || 0}, но адреса отправки НП не вернула. ` +
                    "Введите номер или часть адреса отделения в поле поиска точки и нажмите “Найти refs” снова.",
                );
            }
        } catch (error) {
            const detail = formatApiError(error, "Не удалось получить refs из НП.");
            setStatus(`НП refs не получены: ${detail}`);
        }
    };

    const findExistingSenderPoint = (point) => {
        const addressRef = String(point?.sender_address_ref || "").trim();
        if (!addressRef) {
            return null;
        }
        const pointType = String(point?.point_type || "standard").trim() || "standard";
        return (selectedView?.sender_points || []).find((savedPoint) => (
            String(savedPoint.sender_address_ref || "").trim() === addressRef
            && String(savedPoint.point_type || "standard").trim() === pointType
        )) || null;
    };

    const applyDiscoveredSenderPoint = (point) => {
        const existingPoint = findExistingSenderPoint(point);
        setSettingsDraft((current) => ({
            ...current,
            sender_ref: point.sender_ref || current.sender_ref,
            contact_sender_ref: point.contact_sender_ref || current.contact_sender_ref,
            city_sender_ref: point.city_sender_ref || current.city_sender_ref,
            sender_phone: point.sender_phone || current.sender_phone,
        }));
        setPointDraft((current) => buildPointDraftFromDiscovery(point, existingPoint || current));
        setStatus(
            existingPoint
                ? "Такая точка уже есть в базе. Форма переключена в редактирование существующей точки."
                : "Точка из кабинета НП подставлена. Нажмите “Сохранить”, чтобы сделать её основной.",
        );
    };

    const runWmsCheck = async () => {
        if (!selectedOrgId) {
            return;
        }
        try {
            setStatus("Проверка подключения к WMS Новой почты...");
            setWmsCheck(null);
            const result = await checkNovaPoshtaLegalEntityWms(selectedOrgId);
            setWmsCheck(result);
            setStatus(result.wsdl_ok ? "WMS подключение работает." : `WMS ошибка: ${result.error || "не настроено"}`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось проверить WMS."));
        }
    };

    const selectedOrg = selectedView?.business_organization || null;
    const senderPoints = selectedView?.sender_points || [];

    const orgInitials = (selectedOrg?.short_name || "??")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();

    return (
        <>
            <div className="ip-tabbar">
                {[
                    ["global", "Общие настройки"],
                    ["legal", "Юрлица"],
                ].map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        className={activeTab === key ? "ip-tab active" : "ip-tab"}
                        onClick={() => setActiveTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {status && (
                <div className="ip-status">
                    {status}
                </div>
            )}

            {activeTab === "global" && globalSettings && (
                <section className="ip-card">
                    <div className="ip-card-body">
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                            <div>
                                <h2 className="ip-title">Общие настройки НП</h2>
                                <p className="ip-lead">
                                    Только параметры, которые одинаковы для всех юрлиц. Кабинеты, ключи и адреса отправки настраиваются во вкладке “Юрлица”.
                                </p>
                            </div>
                            <button type="button" className="ip-btn ip-btn-primary" onClick={saveGlobalSettings}>
                                Сохранить
                            </button>
                        </div>

                        <div className="ip-block">
                            <div className="ip-bh"><span className="ip-ic">📦</span><h4>Значения по умолчанию для ТТН</h4></div>
                            <div className="ip-grid4">
                                <label className="ip-fld"><span>Количество мест по умолчанию</span>
                                    <input className="ip-inp" type="number" value={globalSettings.default_seats ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, default_seats: e.target.value })} />
                                    <span className="ip-help-text">Используется при создании обычной ТТН, если заказ не передал точное число мест.</span>
                                </label>
                                <label className="ip-fld"><span>Вес отправления по умолчанию, кг</span>
                                    <input className="ip-inp" value={globalSettings.default_weight_kg ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, default_weight_kg: e.target.value })} />
                                    <span className="ip-help-text">Fallback для ТТН, когда вес не рассчитан из товаров.</span>
                                </label>
                                <label className="ip-fld"><span>Объем отправления по умолчанию, м3</span>
                                    <input className="ip-inp" value={globalSettings.default_volume_m3 ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, default_volume_m3: e.target.value })} />
                                    <span className="ip-help-text">Fallback для ТТН, используется для расчета габаритов места.</span>
                                </label>
                                <label className="ip-fld"><span>Описание отправления по умолчанию</span>
                                    <input className="ip-inp" value={globalSettings.default_description ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, default_description: e.target.value })} />
                                    <span className="ip-help-text">Текст, который попадет в описание ТТН, если нет более точного описания.</span>
                                </label>
                            </div>
                        </div>

                        <div className="ip-block">
                            <div className="ip-bh"><span className="ip-ic">⚑</span><h4>Fallback-юрлицо</h4>
                                <HelpTip
                                    title="Fallback при нераспознанном юрлице"
                                    items={[
                                        ["Когда используется", "Обычно юрлицо берётся из organizationId заказа SalesDrive. Это поле нужно только как аварийный fallback, если заказ не удалось связать с юрлицом."],
                                    ]}
                                />
                            </div>
                            <div className="ip-grid2">
                                <label className="ip-fld ip-col-span-2"><span>Fallback-юрлицо для НП</span>
                                    <select
                                        className="ip-inp"
                                        value={globalSettings.default_business_organization_id || ""}
                                        onChange={(event) => setGlobalSettings({ ...globalSettings, default_business_organization_id: event.target.value })}
                                    >
                                        <option value="">Не выбрано</option>
                                        {legalEntities.map((item) => (
                                            <option key={item.business_organization.id} value={item.business_organization.id}>
                                                {formatOrgTitle(item.business_organization)}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>

                        <div className={`ip-accordion${showAdvancedWms ? " open" : ""}`}>
                            <button type="button" className="ip-ac-head" onClick={() => setShowAdvancedWms((value) => !value)}>
                                <span className="ip-nm">Расширенные настройки WMS</span>
                                <span className="ip-sub">Технические параметры контура fulfillment/WMS. Если WMS не используется, блок можно не заполнять.</span>
                                <span className="ip-chev">⌄</span>
                            </button>
                            {showAdvancedWms && (
                                <div className="ip-ac-body">
                                    <div className="ip-grid4">
                                        <label className="ip-fld ip-col-span-2"><span>WMS endpoint / Адрес SOAP API</span>
                                            <input className="ip-inp" value={globalSettings.wms_base_url ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_base_url: e.target.value })} />
                                            <span className="ip-help-text">Общий URL API WMS Новой почты. Один для всех юрлиц, если НП не выдала отдельные endpoints.</span>
                                        </label>
                                        <label className="ip-fld"><span>WMS timeout / Таймаут, сек</span>
                                            <input className="ip-inp" type="number" value={globalSettings.wms_timeout_seconds ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_timeout_seconds: e.target.value })} />
                                            <span className="ip-help-text">Сколько секунд ждать ответ WMS перед ошибкой.</span>
                                        </label>
                                        <label className="ip-fld"><span>WMS default weight / Вес товара по умолчанию, кг</span>
                                            <input className="ip-inp" value={globalSettings.wms_default_weight_kg ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_default_weight_kg: e.target.value })} />
                                            <span className="ip-help-text">Fallback для D14/WMS товаров, если в каталоге нет веса.</span>
                                        </label>
                                        <label className="ip-fld"><span>WMS length / Длина товара, см</span>
                                            <input className="ip-inp" value={globalSettings.wms_default_length_cm ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_default_length_cm: e.target.value })} />
                                        </label>
                                        <label className="ip-fld"><span>WMS height / Высота товара, см</span>
                                            <input className="ip-inp" value={globalSettings.wms_default_height_cm ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_default_height_cm: e.target.value })} />
                                        </label>
                                        <label className="ip-fld"><span>WMS width / Ширина товара, см</span>
                                            <input className="ip-inp" value={globalSettings.wms_default_width_cm ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_default_width_cm: e.target.value })} />
                                        </label>
                                        <label className="ip-fld"><span>Expiration sign / Учет сроков годности</span>
                                            <input className="ip-inp" value={globalSettings.wms_expiration_date_sign ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_expiration_date_sign: e.target.value })} />
                                            <span className="ip-help-text">Где применяется: отправка товаров в WMS CreateUpdateGoods. Что ставить сейчас: 0, если НП не включила учет сроков годности для вашего fulfillment-склада.</span>
                                        </label>
                                        <label className="ip-fld"><span>Classifier / Единица измерения WMS</span>
                                            <input className="ip-inp" value={globalSettings.wms_accordind_to_classifier ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_accordind_to_classifier: e.target.value })} />
                                            <span className="ip-help-text">Где применяется: CreateUpdateGoods, поле единицы измерения товара. Что ставить сейчас: 796 для 'шт.'.</span>
                                        </label>
                                        <label className="ip-fld"><span>Fact poll interval / Интервал опроса фактов, сек</span>
                                            <input className="ip-inp" type="number" value={globalSettings.wms_fact_poll_interval_seconds ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_fact_poll_interval_seconds: e.target.value })} />
                                            <span className="ip-help-text">Где применяется: scheduler получения фактов приемки из WMS. Что ставить: 300 сек; минимум в коде 30 сек.</span>
                                        </label>
                                        <label className="ip-fld"><span>Fact lookback / Окно поиска фактов, дней</span>
                                            <input className="ip-inp" type="number" value={globalSettings.wms_fact_lookback_days ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_fact_lookback_days: e.target.value })} />
                                            <span className="ip-help-text">Где применяется: scheduler выбирает накладные/планы за последние N дней. Что ставить: 14 дней.</span>
                                        </label>
                                        <label className="ip-fld"><span>Fact max invoices / Лимит накладных за цикл</span>
                                            <input className="ip-inp" type="number" value={globalSettings.wms_fact_max_invoices ?? ""} onChange={(e) => setGlobalSettings({ ...globalSettings, wms_fact_max_invoices: e.target.value })} />
                                            <span className="ip-help-text">Где применяется: scheduler ограничивает нагрузку за один цикл. Что ставить: 50.</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {activeTab === "legal" && (
                <div className="ip-split">
                    <section className="ip-card ip-picker">
                        <h2>Юрлица</h2>
                        <div className="ip-list">
                            {legalEntities.map((item) => {
                                const org = item.business_organization;
                                const active = org.id === selectedOrgId;
                                return (
                                    <button
                                        key={org.id}
                                        type="button"
                                        className={active ? "ip-list-item sel" : "ip-list-item"}
                                        onClick={() => setSelectedOrgId(org.id)}
                                    >
                                        <span className="ip-nm">{org.short_name}</span>
                                        <span className="ip-meta">{org.tax_id || "без ЕДРПОУ"}</span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="ip-picker-note">Список юрлиц наследуется из «Организация → Юрлица и кассы». Здесь настраиваются только реквизиты НП конкретного кабинета.</div>
                    </section>

                    <div className="ip-detail">
                        <section className="ip-card ip-detail-head">
                            <div className="ip-avatar">{orgInitials}</div>
                            <div className="ip-who">
                                <div className="ip-t">{formatOrgTitle(selectedOrg)}</div>
                                <div className="ip-s">
                                    <span className={`ip-badge ${settingsDraft.is_active ? "on" : "off"}`}><span className="ip-dot" />{settingsDraft.is_active ? "Активно" : "Неактивно"}</span>
                                    {settingsDraft.is_default && <span className="ip-badge info"><span className="ip-dot" />Fallback-юрлицо</span>}
                                    <span className={`ip-badge ${settingsDraft.wms_enabled ? "on" : "off"}`}><span className="ip-dot" />{settingsDraft.wms_enabled ? "WMS включен" : "WMS выключен"}</span>
                                </div>
                            </div>
                            <div className="ip-acts">
                                <HelpTip
                                    title="Юрлица НП — как это работает"
                                    items={[
                                        ["Как заказ выбирает юрлицо", "По organizationId заказа SalesDrive. Поставщик определяет, обычная это отправка или fulfillment/WMS."],
                                        ["Импорт из .env", "Не определяет владельца ключей автоматически. Нажимайте только для того юрлица, чьи NP_* значения сейчас реально лежат в .env — если там кабинет Петренко, не импортируйте его в Цимко."],
                                        ["Fallback из env", "После заполнения этой страницы runtime берёт настройки НП из БД. Переменные NP_API_KEY, NP_SENDER_REF, NP_CONTACT_SENDER_REF, NP_SENDER_ADDRESS_REF, NP_CITY_SENDER_REF, NP_FULFILLMENT_*, NP_WMS_* остаются только переходным fallback и источником для импорта — сами значения на этой странице не показываются, и импорт их не меняет."],
                                        ["«Найти refs в кабинете НП»", "Использует API key выбранного юрлица и legacy API 2.0 Новой почты. Если ключ нового формата или НП не разрешила методы Counterparty — refs нужно взять из кабинета/у менеджера и внести вручную."],
                                        ["«Проверить WMS»", "Живой тестовый запрос: проверяет SOAP WMS endpoint, логин и пароль fulfillment для этого юрлица."],
                                    ]}
                                />
                                <button type="button" className="ip-btn ip-btn-primary" onClick={saveLegalSettings}>Сохранить</button>
                            </div>
                        </section>

                        <section className="ip-card">
                            <div className="ip-card-body">
                                <div className="ip-tabbar">
                                    <button type="button" className={legalCardTab === "main" ? "ip-tab active" : "ip-tab"} onClick={() => setLegalCardTab("main")}>Основное</button>
                                    <button type="button" className={legalCardTab === "points" ? "ip-tab active" : "ip-tab"} onClick={() => setLegalCardTab("points")}>Точки отправки ({senderPoints.length})</button>
                                    <button type="button" className={legalCardTab === "wms" ? "ip-tab active" : "ip-tab"} onClick={() => setLegalCardTab("wms")}>Fulfillment / WMS</button>
                                </div>

                                {legalCardTab === "main" && (
                                    <>
                                        <div className="ip-block">
                                            <div className="ip-bh"><span className="ip-ic">🔑</span><h4>Реквизиты кабинета НП</h4>
                                                <div className="ip-bacts">
                                                    <button type="button" className="ip-btn ip-btn-soft-warn ip-btn-sm" onClick={importEnv}>Импорт текущего .env</button>
                                                </div>
                                            </div>
                                            <div className="ip-grid4">
                                                <label className="ip-fld"><span>API key Новой почты / NP API key</span>
                                                    <input className="ip-inp" value={settingsDraft.api_key ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, api_key: e.target.value })} />
                                                    <span className="ip-help-text">Ключ кабинета НП этого юрлица для обычных ТТН.</span>
                                                </label>
                                                <label className="ip-fld"><span>Отправитель / Sender ref</span>
                                                    <input className="ip-inp" value={settingsDraft.sender_ref ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, sender_ref: e.target.value })} />
                                                    <span className="ip-help-text">Ref контрагента-отправителя в кабинете НП.</span>
                                                </label>
                                                <label className="ip-fld"><span>Контакт отправителя / Contact sender ref</span>
                                                    <input className="ip-inp" value={settingsDraft.contact_sender_ref ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, contact_sender_ref: e.target.value })} />
                                                    <span className="ip-help-text">Ref контактного лица отправителя.</span>
                                                </label>
                                                <label className="ip-fld"><span>Город отправителя / City sender ref</span>
                                                    <input className="ip-inp" value={settingsDraft.city_sender_ref ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, city_sender_ref: e.target.value })} />
                                                    <span className="ip-help-text">Ref города отправителя. Может уточняться на уровне точки отправки.</span>
                                                </label>
                                                <label className="ip-fld"><span>Телефон отправителя</span>
                                                    <input className="ip-inp" value={settingsDraft.sender_phone ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, sender_phone: e.target.value })} />
                                                </label>
                                                <label className="ip-fld ip-col-span-2"><span>Описание отправления в ТТН (переопределение)</span>
                                                    <input className="ip-inp" value={settingsDraft.default_description ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, default_description: e.target.value })} />
                                                    <span className="ip-help-text">Текст в поле Description ТТН для этого юрлица. Пусто → берётся глобальное описание по умолчанию.</span>
                                                </label>
                                            </div>
                                        </div>
                                        <div className="ip-block">
                                            <div className="ip-bh"><span className="ip-ic">⚑</span><h4>Статус</h4></div>
                                            <div className="ip-grid2">
                                                <label className={`ip-check${settingsDraft.is_active ? " ck" : ""}`}>
                                                    <input className="ip-box" type="checkbox" checked={Boolean(settingsDraft.is_active)} onChange={(e) => setSettingsDraft({ ...settingsDraft, is_active: e.target.checked })} />
                                                    Активно
                                                </label>
                                                <label className={`ip-check${settingsDraft.is_default ? " ck" : ""}`}>
                                                    <input className="ip-box" type="checkbox" checked={Boolean(settingsDraft.is_default)} onChange={(e) => setSettingsDraft({ ...settingsDraft, is_default: e.target.checked })} />
                                                    Fallback-юрлицо, если заказ не распознан
                                                </label>
                                            </div>
                                        </div>
                                    </>
                                )}

                                {legalCardTab === "points" && (
                                    <>
                                        <div className="ip-block">
                                            <div className="ip-bh"><span className="ip-ic">🔍</span><h4>Поиск отделения в кабинете НП</h4></div>
                                            <div className="ip-grid3">
                                                <label className="ip-fld"><span>Город поиска точки</span>
                                                    <input className="ip-inp" value={warehouseSearch.cityName} onChange={(e) => setWarehouseSearch({ ...warehouseSearch, cityName: e.target.value })} />
                                                    <span className="ip-help-text">Используется, если кабинет НП не отдаёт сохранённые адреса. Например: Київ.</span>
                                                </label>
                                                <label className="ip-fld"><span>Номер/адрес отделения</span>
                                                    <input className="ip-inp" value={warehouseSearch.query} onChange={(e) => setWarehouseSearch({ ...warehouseSearch, query: e.target.value })} />
                                                    <span className="ip-help-text">Например: 195 или Бандери. Без этого поля список отделений не загружается.</span>
                                                </label>
                                                <div className="ip-fld" style={{ alignSelf: "end" }}>
                                                    <button type="button" className="ip-btn" style={{ width: "100%" }} onClick={runDiscovery}>Найти refs в кабинете НП</button>
                                                </div>
                                            </div>
                                        </div>

                                        {discovery && (
                                            <div className="ip-block">
                                                <div className="ip-bh"><span className="ip-ic">✨</span><h4>Найдено в кабинете НП</h4></div>
                                                {Array.isArray(discovery.sender_points) && discovery.sender_points.length > 0 ? (
                                                    <div className="ip-list" style={{ maxHeight: "none" }}>
                                                        {discovery.sender_points.map((point, index) => (
                                                            <button
                                                                key={`${point.sender_address_ref || index}-${point.contact_sender_ref || ""}`}
                                                                type="button"
                                                                className="ip-list-item"
                                                                onClick={() => applyDiscoveredSenderPoint(point)}
                                                            >
                                                                <span className="ip-nm">{point.point_name || point.address_description || `Точка ${index + 1}`}</span>
                                                                <span className="ip-meta">
                                                                    Ref адреса: {point.sender_address_ref || "не задан"} · Контакт: {point.contact_description || point.contact_sender_ref || "не задан"}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <div className="ip-callout warn">
                                                        <span className="ip-ic">⚠</span>
                                                        <span>Кабинет НП вернул отправителя и контакт, но не вернул адреса отправки. Введите номер/адрес отделения в поле поиска выше и нажмите “Найти refs” снова.</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="ip-block">
                                            <div className="ip-bh"><span className="ip-ic">📍</span><h4>Уже сохранено в базе</h4></div>
                                            <div className="ip-list" style={{ maxHeight: "none" }}>
                                                {senderPoints.map((point) => (
                                                    <button
                                                        key={point.id}
                                                        type="button"
                                                        className="ip-list-item"
                                                        onClick={() => setPointDraft({ ...emptyPoint, ...point })}
                                                    >
                                                        <span className="ip-nm">{point.point_name} · {point.point_type === "fulfillment" ? "Fulfillment" : "Обычная"}</span>
                                                        <span className="ip-meta">Ref адреса: {point.sender_address_ref || "не задан"}</span>
                                                        <span className="ip-meta">Склад WMS: {point.wms_dest_warehouse || "склад юрлица по умолчанию"}</span>
                                                        {senderAddressLooksWrong(point) && (
                                                            <span className="ip-meta" style={{ color: "var(--ip-danger)", fontWeight: 700 }}>
                                                                Похоже, здесь сохранён sender/contact ref вместо ref адреса.
                                                            </span>
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="ip-block" style={{ background: "var(--ip-surface)" }}>
                                            <div className="ip-bh"><span className="ip-ic">✎</span><h4>{pointDraft.id ? "Редактирование точки" : "Новая точка"}</h4></div>
                                            <div className="ip-grid4">
                                                <label className="ip-fld"><span>Название точки</span>
                                                    <input className="ip-inp" value={pointDraft.point_name} onChange={(e) => setPointDraft({ ...pointDraft, point_name: e.target.value })} />
                                                    <span className="ip-help-text">Понятное имя для оператора: Основной адрес Петренко, Fulfillment НП и т.п.</span>
                                                </label>
                                                <label className="ip-fld"><span>Тип точки</span>
                                                    <select className="ip-inp" value={pointDraft.point_type} onChange={(event) => setPointDraft({ ...pointDraft, point_type: event.target.value })}>
                                                        <option value="standard">Обычная</option>
                                                        <option value="fulfillment">Fulfillment</option>
                                                    </select>
                                                </label>
                                                <label className="ip-fld"><span>Склад WMS / wms_dest_warehouse</span>
                                                    <input className="ip-inp" value={pointDraft.wms_dest_warehouse} onChange={(e) => setPointDraft({ ...pointDraft, wms_dest_warehouse: e.target.value })} />
                                                    <span className="ip-help-text">Склад фулфилмента, с которого отгружает этот отправитель (D14 outbound). Пусто → берётся склад по умолчанию юрлица.</span>
                                                </label>
                                                <label className="ip-fld"><span>Адрес отправителя / Sender address ref</span>
                                                    <input className="ip-inp" value={pointDraft.sender_address_ref} onChange={(e) => setPointDraft({ ...pointDraft, sender_address_ref: e.target.value })} />
                                                    <span className="ip-help-text">Ref адреса/отделения отправки в кабинете НП.</span>
                                                </label>
                                                <label className="ip-fld"><span>Город точки / City sender ref</span>
                                                    <input className="ip-inp" value={pointDraft.city_sender_ref} onChange={(e) => setPointDraft({ ...pointDraft, city_sender_ref: e.target.value })} />
                                                </label>
                                                <label className="ip-fld"><span>Отправитель точки / Sender ref</span>
                                                    <input className="ip-inp" value={pointDraft.sender_ref} onChange={(e) => setPointDraft({ ...pointDraft, sender_ref: e.target.value })} />
                                                    <span className="ip-help-text">Можно оставить пустым, тогда будет взят sender ref из настроек юрлица.</span>
                                                </label>
                                                <label className="ip-fld"><span>Контакт точки / Contact sender ref</span>
                                                    <input className="ip-inp" value={pointDraft.contact_sender_ref} onChange={(e) => setPointDraft({ ...pointDraft, contact_sender_ref: e.target.value })} />
                                                    <span className="ip-help-text">Можно оставить пустым, тогда будет взят contact ref из настроек юрлица.</span>
                                                </label>
                                                <label className="ip-fld ip-col-span-2"><span>Заметки</span>
                                                    <input className="ip-inp" value={pointDraft.notes} onChange={(e) => setPointDraft({ ...pointDraft, notes: e.target.value })} />
                                                </label>
                                                <label className={`ip-check${pointDraft.is_default ? " ck" : ""}`}>
                                                    <input className="ip-box" type="checkbox" checked={Boolean(pointDraft.is_default)} onChange={(e) => setPointDraft({ ...pointDraft, is_default: e.target.checked })} />
                                                    Основная точка обычной отправки
                                                </label>
                                                <label className={`ip-check${pointDraft.is_fulfillment_default ? " ck" : ""}`}>
                                                    <input className="ip-box" type="checkbox" checked={Boolean(pointDraft.is_fulfillment_default)} onChange={(e) => setPointDraft({ ...pointDraft, is_fulfillment_default: e.target.checked })} />
                                                    Основная fulfillment-точка
                                                </label>
                                                <label className={`ip-check${pointDraft.is_active ? " ck" : ""}`}>
                                                    <input className="ip-box" type="checkbox" checked={Boolean(pointDraft.is_active)} onChange={(e) => setPointDraft({ ...pointDraft, is_active: e.target.checked })} />
                                                    Активна
                                                </label>
                                            </div>
                                            <div className="ip-save-bar">
                                                <button type="button" className="ip-btn ip-btn-primary" onClick={savePoint}>Сохранить точку</button>
                                                <button type="button" className="ip-btn" onClick={() => setPointDraft(emptyPoint)}>Очистить форму</button>
                                            </div>
                                        </div>

                                        {discovery && (
                                            <pre style={{ margin: 0, padding: "12px", borderRadius: "8px", backgroundColor: "var(--ip-surface-2)", overflow: "auto", maxHeight: "320px" }}>
                                                {JSON.stringify(discovery, null, 2)}
                                            </pre>
                                        )}
                                    </>
                                )}

                                {legalCardTab === "wms" && (
                                    <>
                                        <label className={`ip-check ck`} style={{ minHeight: "48px", padding: "10px 16px", fontSize: "14.5px", marginBottom: "4px" }}>
                                            <input
                                                className="ip-box"
                                                type="checkbox"
                                                checked={Boolean(settingsDraft.wms_enabled)}
                                                onChange={(e) => setSettingsDraft(
                                                    clearDisabledWmsFields({ ...settingsDraft, wms_enabled: e.target.checked }, ""),
                                                )}
                                            />
                                            WMS включен
                                        </label>

                                        {settingsDraft.wms_enabled ? (
                                            <>
                                                <div className="ip-block">
                                                    <div className="ip-bh"><span className="ip-ic">⚙</span><h4>Параметры WMS</h4><span className="ip-hint">только для fulfillment-поставщиков</span></div>
                                                    <div className="ip-grid3">
                                                        <label className="ip-fld"><span>Способ авторизации WMS / Auth mode</span>
                                                            <input className="ip-inp" value="basic - логин/пароль" disabled readOnly />
                                                            <span className="ip-help-text">Фиксируем рабочий режим: basic, логин и пароль от WMS. Если НП изменит схему авторизации, доработаем отдельно.</span>
                                                        </label>
                                                        <label className="ip-fld"><span>Организация WMS / WMS organization</span>
                                                            <input className="ip-inp" value={settingsDraft.wms_organization ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, wms_organization: e.target.value })} />
                                                            <span className="ip-help-text">Код организации/depositor, который НП использует в WMS SOAP документах.</span>
                                                        </label>
                                                        <label className="ip-fld"><span>Логин WMS / WMS login</span>
                                                            <input className="ip-inp" value={settingsDraft.wms_login ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, wms_login: e.target.value })} />
                                                        </label>
                                                        <label className="ip-fld"><span>Пароль WMS / WMS password</span>
                                                            <input className="ip-inp" value={settingsDraft.wms_password ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, wms_password: e.target.value })} />
                                                        </label>
                                                        <label className="ip-fld ip-col-span-2"><span>Склад назначения WMS / Default warehouse</span>
                                                            <input className="ip-inp" value={settingsDraft.wms_default_dest_warehouse ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, wms_default_dest_warehouse: e.target.value })} />
                                                            <span className="ip-help-text">Склад fulfillment, куда WMS отправляет заказ или план приемки.</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="ip-callout">
                                                    <span className="ip-ic">ℹ</span>
                                                    <span>Для D14/fulfillment заказов runtime берет Sender ref из настроек юрлица или fulfillment-точки, TTNOrganization = API key Новой почты, а склад отправки определяет WMS warehouse ({settingsDraft.wms_default_dest_warehouse || "не задан"}).</span>
                                                </div>

                                                <div className={`ip-accordion${showLegalAdvanced ? " open" : ""}`}>
                                                    <button type="button" className="ip-ac-head" onClick={() => setShowLegalAdvanced((value) => !value)}>
                                                        <span className="ip-nm">Редкие поля</span>
                                                        <span className="ip-sub">Заполнять только если НП явно попросила отдельный режим</span>
                                                        <span className="ip-chev">⌄</span>
                                                    </button>
                                                    {showLegalAdvanced && (
                                                        <div className="ip-ac-body">
                                                            <div className="ip-grid3">
                                                                <label className="ip-fld"><span>TTNOrganization override</span>
                                                                    <input className="ip-inp" value={settingsDraft.wms_ttn_organization ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, wms_ttn_organization: e.target.value })} />
                                                                    <span className="ip-help-text">Заполнять только если НП отдельно сказала использовать другой API key для CreateUpdateOrders. Обычно пусто.</span>
                                                                </label>
                                                                <label className="ip-fld"><span>Fulfillment API key</span>
                                                                    <input className="ip-inp" value={settingsDraft.fulfillment_api_key ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, fulfillment_api_key: e.target.value })} />
                                                                    <span className="ip-help-text">Справочно/резервно. В текущем WMS basic-flow для ТТН не используется.</span>
                                                                </label>
                                                                <label className="ip-fld"><span>Fulfillment/access key от НП</span>
                                                                    <input className="ip-inp" value={settingsDraft.wms_access_key ?? ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, wms_access_key: e.target.value })} />
                                                                    <span className="ip-help-text">Справочно/резервно. Основная WMS авторизация сейчас идет по login/password.</span>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="ip-save-bar">
                                                    <button type="button" className="ip-btn" onClick={runWmsCheck}>Проверить WMS</button>
                                                    {wmsCheck && (
                                                        <span className="ip-note">{wmsCheck.wsdl_ok ? "✓ WMS подключение работает." : `✗ WMS ошибка: ${wmsCheck.error || "не настроено"}`}</span>
                                                    )}
                                                </div>

                                                {!discovery && wmsCheck && (
                                                    <pre style={{ margin: 0, padding: "12px", borderRadius: "8px", backgroundColor: "var(--ip-surface-2)", overflow: "auto", maxHeight: "320px" }}>
                                                        {JSON.stringify(wmsCheck, null, 2)}
                                                    </pre>
                                                )}
                                            </>
                                        ) : (
                                            <div className="ip-callout muted">
                                                <span className="ip-ic">ℹ</span>
                                                <span>Fulfillment/WMS для этого юрлица выключен. Поля WMS будут очищены при сохранении и не используются для обычных ТТН.</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </section>
                    </div>
                </div>
            )}
        </>
    );
};

const CheckboxCreateReceiptPanel = () => {
    const [orderNumber, setOrderNumber] = useState("");
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    const submit = async () => {
        const value = orderNumber.trim();
        if (!value) {
            setError("Укажите номер заявки SalesDrive.");
            setResult(null);
            return;
        }
        setRunning(true);
        setError("");
        setResult(null);
        try {
            const data = await forceCreateCheckboxReceipt(value);
            setResult(data);
        } catch (err) {
            setError(formatApiError(err, "Не удалось создать чек."));
        } finally {
            setRunning(false);
        }
    };

    return (
        <section className="ip-card">
            <div className="ip-card-body">
                <div className="ip-block" style={{ background: "var(--ip-surface)" }}>
                    <div className="ip-bh"><span className="ip-ic">➕</span><h4>Создать чек по заявке</h4></div>
                    <p className="ip-lead" style={{ margin: 0 }}>
                        Введите номер заявки SalesDrive и нажмите «Создать чек». Чек будет фискализован в Checkbox,
                        ссылка отправлена в заказ SalesDrive и в Prom. Если чек по заявке уже создан — появится ошибка.
                    </p>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <label className="ip-fld" style={{ minWidth: "220px" }}>
                            <span>Номер заявки SalesDrive</span>
                            <input
                                className="ip-inp"
                                value={orderNumber}
                                onChange={(event) => setOrderNumber(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" && !running) submit();
                                }}
                                placeholder="Напр. 12345"
                                disabled={running}
                            />
                        </label>
                        <button type="button" className="ip-btn ip-btn-primary" onClick={submit} disabled={running}>
                            {running ? "Создание..." : "Создать чек"}
                        </button>
                    </div>
                    {error ? (
                        <div className="ip-callout danger">
                            <span className="ip-ic">✕</span>
                            <span>{error}</span>
                        </div>
                    ) : null}
                    {result ? (
                        <div className="ip-callout good">
                            <span className="ip-ic">✓</span>
                            <span>
                                <div style={{ fontWeight: 700 }}>Чек создан для заявки {result.salesdrive_order_id}.</div>
                                {result.fiscal_code ? <div>Фискальный код: {result.fiscal_code}</div> : null}
                                {result.receipt_url ? (
                                    <div>
                                        Ссылка:{" "}
                                        <a href={result.receipt_url} target="_blank" rel="noreferrer">
                                            {result.receipt_url}
                                        </a>
                                    </div>
                                ) : null}
                            </span>
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
};

const CheckboxIntegrationPage = () => {
    const navigate = useNavigate();
    const [tab, setTab] = useState("settings");
    const [settings, setSettings] = useState(null);
    const [accessKey, setAccessKey] = useState("");
    const [clearAccessKey, setClearAccessKey] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [status, setStatus] = useState("");

    const loadData = useCallback(async () => {
        setSettings(await getCheckboxGlobalSettings());
    }, []);

    useEffect(() => {
        loadData().catch((error) => setStatus(formatApiError(error, "Не удалось загрузить настройки Checkbox.")));
    }, [loadData]);

    const update = (field, value) => setSettings((current) => ({ ...current, [field]: value }));
    const excludedSuppliersText = Array.isArray(settings?.excluded_suppliers)
        ? settings.excluded_suppliers.join(", ")
        : String(settings?.excluded_suppliers || "");

    const saveSettings = async () => {
        try {
            setStatus("Сохранение настроек Checkbox...");
            const payload = {
                ...settings,
                access_key: nullable(accessKey),
                clear_access_key: clearAccessKey,
                excluded_suppliers: String(settings.excluded_suppliers || "")
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean),
                default_payment_method_id: Number(settings.default_payment_method_id),
                default_tax_code: settings.default_tax_code === "" ? null : Number(settings.default_tax_code),
                receipt_poll_interval_sec: positiveNumberOrDefault(settings.receipt_poll_interval_sec, 2),
                receipt_poll_timeout_sec: positiveNumberOrDefault(settings.receipt_poll_timeout_sec, 30),
                receipt_retry_max_attempts: positiveNumberOrDefault(settings.receipt_retry_max_attempts, 5),
            };
            delete payload.source;
            delete payload.access_key_set;
            const saved = await updateCheckboxGlobalSettings(payload);
            setSettings(saved);
            setAccessKey("");
            setClearAccessKey(false);
            setStatus("Настройки Checkbox сохранены.");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сохранить настройки Checkbox."));
        }
    };

    const importEnv = async () => {
        if (!window.confirm("Импортировать текущие CHECKBOX_* значения из .env в глобальные настройки Checkbox?")) {
            return;
        }
        try {
            setStatus("Импорт настроек Checkbox из .env...");
            const result = await importCheckboxEnvSettings();
            await loadData();
            setStatus(`Импортировано полей: ${result.imported_fields.length}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось импортировать настройки Checkbox."));
        }
    };

    const tabs = [
        ["settings", "Настройки"],
        ["create-receipt", "Создать чек"],
    ];

    return (
        <div className="integrations-page" style={{ display: "grid", gap: "12px" }}>
            <div className="ip-tabbar">
                {tabs.map(([key, label]) => (
                    <button
                        key={key}
                        type="button"
                        className={tab === key ? "ip-tab active" : "ip-tab"}
                        onClick={() => setTab(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {tab === "create-receipt" ? (
                <CheckboxCreateReceiptPanel />
            ) : !settings ? (
                <section className="ip-card">
                    <div className="ip-card-body">Загрузка настроек Checkbox...</div>
                </section>
            ) : (
                <section className="ip-card">
                    <div className="ip-card-body">
                        <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                            <div>
                                <h2 className="ip-title">Общие настройки Checkbox</h2>
                                <p className="ip-lead">Глобальные параметры фискализации. Ключи и режимы конкретных касс остаются у юрлиц.</p>
                            </div>
                            <div className="ip-acts">
                                <span className="ip-badge info">{settings.source === "db" ? "DB" : "ENV fallback"}</span>
                                <HelpTip
                                    title="Checkbox — на что влияют поля"
                                    items={[
                                        ["Access key", "Общий X-Access-Key интеграции Checkbox. Оставьте поле пустым при сохранении, чтобы не менять сохранённый ключ."],
                                        ["Исключённые поставщики", "Fallback-список кодов через запятую. Исключения конкретного юрлица и кассы настраиваются отдельно, в «Организация → Юрлица и кассы»."],
                                        ["Импорт из .env", "Переносит текущие CHECKBOX_* значения из .env в глобальные настройки Checkbox. Разовая операция для перехода на DB-first."],
                                    ]}
                                />
                                <button type="button" className="ip-btn ip-btn-sm" onClick={importEnv}>Импорт текущего .env</button>
                                <button type="button" className="ip-btn ip-btn-primary" onClick={saveSettings}>Сохранить</button>
                            </div>
                        </div>
                        {status ? <div className="ip-status">{status}</div> : null}
                        <div className="ip-callout warn">
                            <span className="ip-ic">⚠</span>
                            <span>Выключение интеграции блокирует новые чеки. Retry ранее созданных чеков и закрытие открытых смен продолжают работать.</span>
                        </div>
                        <div className="ip-block">
                            <div className="ip-check-row">
                                <label className={`ip-check${settings.is_enabled ? " ck" : ""}`}>
                                    <input className="ip-box" type="checkbox" checked={Boolean(settings.is_enabled)} onChange={(e) => update("is_enabled", e.target.checked)} />
                                    Фискализация включена
                                </label>
                                <label className={`ip-check${clearAccessKey ? " ck" : ""}`}>
                                    <input className="ip-box" type="checkbox" checked={Boolean(clearAccessKey)} onChange={(e) => setClearAccessKey(e.target.checked)} />
                                    Удалить сохранённый access key
                                </label>
                            </div>
                            <div className="ip-grid4">
                                <label className="ip-fld ip-col-span-2"><span>Access key</span>
                                    <input className="ip-inp" type="password" value={accessKey} onChange={(e) => setAccessKey(e.target.value)} />
                                    <span className="ip-help-text">{settings.access_key_set ? "Ключ сохранён. Оставьте поле пустым, чтобы не менять." : "Общий X-Access-Key интеграции Checkbox."}</span>
                                </label>
                                <label className="ip-fld"><span>Payment method ID по умолчанию</span>
                                    <input className="ip-inp" type="number" value={settings.default_payment_method_id ?? ""} onChange={(e) => update("default_payment_method_id", e.target.value)} />
                                </label>
                                <label className="ip-fld"><span>Tax code по умолчанию</span>
                                    <input className="ip-inp" type="number" value={settings.default_tax_code ?? ""} onChange={(e) => update("default_tax_code", e.target.value)} />
                                    <span className="ip-help-text">Пустое значение отключает передачу tax.</span>
                                </label>
                                <label className="ip-fld ip-col-span-2"><span>Исключённые поставщики</span>
                                    <input className="ip-inp" value={excludedSuppliersText} onChange={(e) => update("excluded_suppliers", e.target.value)} />
                                    <span className="ip-help-text">Fallback-список кодов через запятую. Исключения юрлица и кассы добавляются отдельно.</span>
                                </label>
                            </div>
                        </div>
                        <div className="ip-block">
                            <div className="ip-bh"><span className="ip-ic">🔔</span><h4>SalesDrive и Telegram</h4></div>
                            <div className="ip-check-row">
                                <label className={`ip-check${settings.salesdrive_update_check_enabled ? " ck" : ""}`}>
                                    <input className="ip-box" type="checkbox" checked={Boolean(settings.salesdrive_update_check_enabled)} onChange={(e) => update("salesdrive_update_check_enabled", e.target.checked)} />
                                    Обновлять ссылку на чек в SalesDrive
                                </label>
                                <label className={`ip-check${settings.telegram_notifications_enabled ? " ck" : ""}`}>
                                    <input className="ip-box" type="checkbox" checked={Boolean(settings.telegram_notifications_enabled)} onChange={(e) => update("telegram_notifications_enabled", e.target.checked)} />
                                    Telegram уведомления Checkbox
                                </label>
                                <label className={`ip-check${settings.telegram_receipt_notifications_enabled ? " ck" : ""}`}>
                                    <input className="ip-box" type="checkbox" checked={Boolean(settings.telegram_receipt_notifications_enabled)} onChange={(e) => update("telegram_receipt_notifications_enabled", e.target.checked)} />
                                    Telegram по чекам
                                </label>
                                <label className={`ip-check${settings.telegram_shift_notifications_enabled ? " ck" : ""}`}>
                                    <input className="ip-box" type="checkbox" checked={Boolean(settings.telegram_shift_notifications_enabled)} onChange={(e) => update("telegram_shift_notifications_enabled", e.target.checked)} />
                                    Telegram по сменам
                                </label>
                            </div>
                            <div className="ip-grid4">
                                <label className="ip-fld" style={{ maxWidth: "320px" }}><span>Поле ссылки в SalesDrive</span>
                                    <input className="ip-inp" value={settings.salesdrive_check_field ?? ""} onChange={(e) => update("salesdrive_check_field", e.target.value)} />
                                </label>
                            </div>
                        </div>
                        <div className={`ip-accordion${showAdvanced ? " open" : ""}`}>
                            <button type="button" className="ip-ac-head" onClick={() => setShowAdvanced((value) => !value)}>
                                <span className="ip-nm">Технические параметры</span>
                                <span className="ip-sub">API base URL, клиент, интервалы poll/retry</span>
                                <span className="ip-chev">⌄</span>
                            </button>
                            {showAdvanced ? (
                                <div className="ip-ac-body">
                                    <div className="ip-grid4">
                                        <label className="ip-fld ip-col-span-2"><span>API base URL</span>
                                            <input className="ip-inp" value={settings.api_base_url ?? ""} onChange={(e) => update("api_base_url", e.target.value)} />
                                        </label>
                                        <label className="ip-fld"><span>Client name</span>
                                            <input className="ip-inp" value={settings.client_name ?? ""} onChange={(e) => update("client_name", e.target.value)} />
                                        </label>
                                        <label className="ip-fld"><span>Client version</span>
                                            <input className="ip-inp" value={settings.client_version ?? ""} onChange={(e) => update("client_version", e.target.value)} />
                                        </label>
                                        <label className="ip-fld"><span>Receipt poll interval, сек</span>
                                            <input className="ip-inp" type="number" value={settings.receipt_poll_interval_sec ?? ""} onChange={(e) => update("receipt_poll_interval_sec", e.target.value)} />
                                        </label>
                                        <label className="ip-fld"><span>Receipt poll timeout, сек</span>
                                            <input className="ip-inp" type="number" value={settings.receipt_poll_timeout_sec ?? ""} onChange={(e) => update("receipt_poll_timeout_sec", e.target.value)} />
                                        </label>
                                        <label className="ip-fld"><span>Retry max attempts</span>
                                            <input className="ip-inp" type="number" value={settings.receipt_retry_max_attempts ?? ""} onChange={(e) => update("receipt_retry_max_attempts", e.target.value)} />
                                        </label>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <div className="ip-save-bar">
                            <button type="button" className="ip-btn" onClick={() => navigate("/organization/legal-entities")}>Перейти к кассам юрлиц</button>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
};

const emptyPromAccount = {
    id: null,
    account_code: "main",
    account_name: "Prom.ua",
    business_organization_id: "",
    is_active: true,
    catalog_enabled: true,
    stock_price_enabled: true,
    live_send_enabled: false,
    sandbox_mode: false,
    base_url: "https://my.prom.ua/api/v1",
    seller_external_id: "",
    api_token: "",
    default_language: "uk",
    feed_public_url: "",
    feed_format: "yml",
    feed_content_profile: "basic",
    mark_missing_product_as: "not_available",
    feed_product_limit: 1000,
    prom_product_scan_limit: 1000,
    catalog_schedule_interval_sec: 3600,
    stock_price_schedule_interval_sec: 900,
    notes: "",
};

const PROM_STOCK_PRICE_INTERVAL_PRESETS = [
    { label: "5 мин", value: 300 },
    { label: "15 мин", value: 900 },
    { label: "30 мин", value: 1800 },
    { label: "1 час", value: 3600 },
];

const PROM_ROOT_GROUP = {
    id: "154607236",
    name: "Корневая группа",
};

const PromIntegrationPage = () => {
    const [accounts, setAccounts] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState(null);
    const [draft, setDraft] = useState(emptyPromAccount);
    const [suppliers, setSuppliers] = useState([]);
    const [supplierRegions, setSupplierRegions] = useState([]);
    const [promRegions, setPromRegions] = useState([]);
    const [regionSearch, setRegionSearch] = useState("");
    const [regionDrafts, setRegionDrafts] = useState({});
    const [showSupplierRegions, setShowSupplierRegions] = useState(false);
    const [editingSupplierRegions, setEditingSupplierRegions] = useState("");
    const [jobs, setJobs] = useState([]);
    const [categoryMappings, setCategoryMappings] = useState([]);
    const [showCategoryAdvanced, setShowCategoryAdvanced] = useState(false);
    const [internalCategoryLevel, setInternalCategoryLevel] = useState("2");
    const [internalCategorySelection, setInternalCategorySelection] = useState("");
    const [internalCategorySearch, setInternalCategorySearch] = useState("");
    const [internalCategoryDropdownOpen, setInternalCategoryDropdownOpen] = useState(false);
    const [internalCategories, setInternalCategories] = useState([]);
    const [platformCategorySearch, setPlatformCategorySearch] = useState("");
    const [platformCategoryDropdownOpen, setPlatformCategoryDropdownOpen] = useState(false);
    const [platformCategories, setPlatformCategories] = useState([]);
    const [platformQuickCategories, setPlatformQuickCategories] = useState([]);
    const [platformCategoryFile, setPlatformCategoryFile] = useState(null);
    const [showCategoryMappings, setShowCategoryMappings] = useState(false);
    const [categoryDraft, setCategoryDraft] = useState({
        internal_category_code: "",
        internal_category_level: "",
        platform_group_id: PROM_ROOT_GROUP.id,
        platform_group_name_ua: PROM_ROOT_GROUP.name,
        platform_category_id: "",
        platform_category_url: "",
        is_active: true,
    });
    const [scopeFile, setScopeFile] = useState(null);
    const [showFeedHistory, setShowFeedHistory] = useState(false);
    const [runningAction, setRunningAction] = useState("");
    const [status, setStatus] = useState("");
    const [promSection, setPromSection] = useState("general");
    const promPricingMode = useMarketplacePricingMode("prom", selectedAccountId);

    const loadAccounts = useCallback(async () => {
        const [data, orgRows] = await Promise.all([
            getPromAccounts(),
            getBusinessOrganizations({ is_active: true }),
        ]);
        setAccounts(data);
        setOrganizations(orgRows);
        setSelectedAccountId((current) => current || data[0]?.id || null);
    }, []);

    useEffect(() => {
        loadAccounts().catch((error) => setStatus(formatApiError(error, "Не удалось загрузить Prom accounts.")));
    }, [loadAccounts]);

    const selectedAccount = useMemo(
        () => accounts.find((item) => item.id === selectedAccountId) || null,
        [accounts, selectedAccountId],
    );

    const loadAccountDetails = useCallback(async () => {
        if (!selectedAccountId) {
            setSuppliers([]);
            setJobs([]);
            return;
        }
        const [supplierRows, jobRows, platformCategoryRows, quickCategoryRows, supplierRegionRows, regionRows] = await Promise.all([
            getPromSupplierSettings(selectedAccountId),
            getPromJobs(selectedAccountId),
            getPromPlatformCategories(selectedAccountId, { limit: 100 }),
            getPromPlatformCategories(selectedAccountId, { quick: true, limit: 100 }),
            getPromSupplierRegions(selectedAccountId),
            getPromRegions(),
        ]);
        const mappingRows = await getPromCategoryMappings(selectedAccountId);
        setSuppliers(supplierRows);
        setJobs(jobRows);
        setPlatformCategories(platformCategoryRows || []);
        setPlatformQuickCategories(quickCategoryRows || []);
        setCategoryMappings(mappingRows);
        setSupplierRegions(supplierRegionRows || []);
        setPromRegions(regionRows || []);
    }, [selectedAccountId]);

    useEffect(() => {
        let cancelled = false;
        getPromRegions(regionSearch).then((rows) => { if (!cancelled) setPromRegions(rows || []); }).catch(() => {});
        return () => { cancelled = true; };
    }, [regionSearch]);

    useEffect(() => {
        setDraft({ ...emptyPromAccount, ...(selectedAccount || {}) });
        loadAccountDetails().catch((error) => setStatus(formatApiError(error, "Не удалось загрузить детали Prom.")));
    }, [selectedAccount, loadAccountDetails]);

    useEffect(() => {
        if (!selectedAccountId) {
            setInternalCategories([]);
            return undefined;
        }
        let cancelled = false;
        getPromInternalCategories(selectedAccountId, {
            level: Number(internalCategoryLevel),
            limit: 300,
        })
            .then((result) => {
                if (!cancelled) {
                    setInternalCategories(result || []);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    setStatus(formatApiError(error, "Не удалось загрузить наши категории."));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [selectedAccountId, internalCategoryLevel]);

    const saveAccount = async () => {
        try {
            setStatus("Сохранение Prom account...");
            const payload = {
                ...draft,
                business_organization_id: draft.business_organization_id ? Number(draft.business_organization_id) : null,
                feed_product_limit: positiveNumberOrDefault(draft.feed_product_limit, 1000),
                prom_product_scan_limit: Math.min(10000, Math.max(100, positiveNumberOrDefault(draft.prom_product_scan_limit, 1000))),
                catalog_schedule_interval_sec: positiveNumberOrDefault(draft.catalog_schedule_interval_sec, 3600),
                stock_price_schedule_interval_sec: positiveNumberOrDefault(draft.stock_price_schedule_interval_sec, 900),
                api_token: nullable(draft.api_token),
            };
            delete payload.id;
            delete payload.platform;
            delete payload.api_token_set;
            delete payload.created_at;
            delete payload.updated_at;
            delete payload.last_catalog_run_at;
            delete payload.last_stock_price_run_at;
            const saved = draft.id
                ? await updatePromAccount(draft.id, payload)
                : await createPromAccount(payload);
            await loadAccounts();
            setSelectedAccountId(saved.id);
            setStatus("Prom account сохранён.");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сохранить Prom account."));
        }
    };

    const uploadScope = async () => {
        if (!selectedAccountId || !scopeFile) {
            setStatus("Выберите Prom account и Excel-файл со SKU.");
            return;
        }
        try {
            setRunningAction("scope_upload");
            setStatus(`Загрузка Excel-ограничения Prom началась: ${formatDateTime(new Date())}.`);
            const result = await uploadPromProductScope(selectedAccountId, scopeFile, null);
            setStatus(`Excel scope загружен ${formatDateTime(new Date())}: SKU ${result.imported}, дублей ${result.duplicates}, пропущено ${result.skipped}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось загрузить Excel scope."));
        } finally {
            setRunningAction("");
        }
    };

    const clearScope = async () => {
        if (!selectedAccountId) {
            setStatus("Выберите Prom account.");
            return;
        }
        if (!window.confirm("Очистить scope? В feed будут выгружаться ВСЕ товары с офферами (в пределах лимита), без приоритета из Excel.")) {
            return;
        }
        try {
            setRunningAction("scope_clear");
            const result = await clearPromProductScope(selectedAccountId);
            setStatus(`Scope очищен ${formatDateTime(new Date())}: удалено ${result.cleared} SKU. Теперь в feed идут все товары с офферами (до лимита «Макс товаров в feed»).`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось очистить scope."));
        } finally {
            setRunningAction("");
        }
    };

    const runFeed = async (dryRun = false) => {
        if (!selectedAccountId) {
            return;
        }
        try {
            setRunningAction("feed_generate");
            setStatus(`${dryRun ? "Проверка" : "Генерация"} Prom feed началась: ${formatDateTime(new Date())}.`);
            const result = await generatePromFeed(selectedAccountId, {
                limit: positiveNumberOrDefault(draft.feed_product_limit, 1000),
                rebuild_snapshot: true,
                dry_run: dryRun,
                content_profile: dryRun ? "enriched" : (draft.feed_content_profile || "basic"),
            });
            await loadAccountDetails();
            setStatus(
                `Prom ${dryRun ? "dry-run завершён" : "feed готов"} ${formatDateTime(new Date())}: job ${result.job_id}, в feed ${result.processed}, skipped ${result.skipped}, errors ${result.errors}. ` +
                `Файл: ${result.feed_file_path || "нет"}` +
                (Object.keys(result.diagnostics || {}).length ? ` Диагностика: ${Object.entries(result.diagnostics).map(([key, value]) => `${key}=${value}`).join(", ")}.` : ""),
            );
        } catch (error) {
            if (error?.response?.status === 409 && error?.response?.data?.detail?.code === "feed_generation_in_progress") {
                const jobId = error.response.data.detail.job_id;
                await loadAccountDetails();
                setStatus(`Prom feed уже генерируется${jobId ? `: job ${jobId}` : ""}. Дождитесь завершения текущей выгрузки.`);
                return;
            }
            setStatus(formatApiError(error, "Не удалось сгенерировать Prom feed."));
        } finally {
            setRunningAction("");
        }
    };

    const saveSupplierRegions = async (supplierCode) => {
        try {
            const current = supplierRegions.filter((item) => item.supplier_code === supplierCode).map((item) => item.region.id);
            const ids = regionDrafts[supplierCode] ?? current;
            await updatePromSupplierRegions(selectedAccountId, supplierCode, ids);
            setRegionDrafts((currentDrafts) => ({ ...currentDrafts, [supplierCode]: ids }));
            await loadAccountDetails();
            setEditingSupplierRegions("");
            setStatus(`${supplierCode}: сохранено городов наличия — ${ids.length}/3.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сохранить города наличия."));
        }
    };

    const feedJobs = jobs.filter((job) => job.job_type === "feed_generate");
    const linkJobs = jobs.filter((job) => job.job_type === "prom_product_links_sync");
    const stockPriceJobs = jobs.filter((job) => job.job_type === "prom_stock_price_sync");
    const latestFeedJob = feedJobs[0] || null;
    const latestLinksJob = linkJobs[0] || null;
    const latestStockPriceJob = stockPriceJobs[0] || null;
    const latestFeedUrlRaw = latestFeedJob?.response_payload?.feed_public_url
        || (selectedAccount?.account_code
            ? `${window.location.origin}/developer_panel/marketplace-feeds/prom/${selectedAccount.account_code}/latest.xml`
            : "");
    const latestFeedUrl = latestFeedUrlRaw?.startsWith("/")
        ? `${window.location.origin}${latestFeedUrlRaw}`
        : latestFeedUrlRaw;

    const copyLatestFeedUrl = async () => {
        if (!latestFeedUrl) {
            setStatus("Сначала сгенерируйте feed.");
            return;
        }
        await navigator.clipboard.writeText(latestFeedUrl);
        setStatus("URL feed скопирован.");
    };

    const runProductLinksSync = async () => {
        if (!selectedAccountId) {
            return;
        }
        try {
            setRunningAction("product_links_sync");
            setStatus(`Синхронизация связок товаров Prom началась: ${formatDateTime(new Date())}.`);
            const result = await syncPromProductLinks(selectedAccountId, { rebuild_snapshot: true });
            await loadAccountDetails();
            setStatus(`Связки товаров Prom готовы ${formatDateTime(new Date())}: job ${result.job_id}, найдено ${result.processed}, не найдено ${result.skipped}, errors ${result.errors}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось синхронизировать связки товаров Prom."));
        } finally {
            setRunningAction("");
        }
    };

    const runStockPriceSync = async () => {
        if (!selectedAccountId) {
            return;
        }
        try {
            setRunningAction("stock_price_sync");
            setStatus(`Обновление цен/остатков через Prom API началось: ${formatDateTime(new Date())}.`);
            const result = await syncPromStockPrices(selectedAccountId, { rebuild_snapshot: true });
            await loadAccountDetails();
            setStatus(`API цены/остатки завершено ${formatDateTime(new Date())}: job ${result.job_id}, обновлено ${result.processed}, skipped ${result.skipped}, errors ${result.errors}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось обновить цены/остатки через Prom API."));
        } finally {
            setRunningAction("");
        }
    };

    const saveCategoryMapping = async () => {
        if (!selectedAccountId) {
            return;
        }
        const platformGroupId = categoryDraft.platform_group_id || PROM_ROOT_GROUP.id;
        const platformGroupName = categoryDraft.platform_group_name_ua || PROM_ROOT_GROUP.name;
        try {
            if (internalCategorySelection !== "__all__" && !categoryDraft.internal_category_code) {
                setStatus("Выберите нашу категорию или пункт “Все категории уровня”.");
                return;
            }
            if (!categoryDraft.platform_category_id && !categoryDraft.platform_category_url) {
                setStatus("Выберите категорию Prom.");
                return;
            }
            const basePayload = {
                ...categoryDraft,
                internal_category_level: categoryDraft.internal_category_level === "" ? null : Number(categoryDraft.internal_category_level),
                platform_group_id: nullable(platformGroupId),
                platform_group_name_ua: nullable(platformGroupName),
                platform_category_id: nullable(categoryDraft.platform_category_id),
                platform_category_url: nullable(categoryDraft.platform_category_url),
            };
            if (internalCategorySelection === "__all__") {
                const rows = await getPromInternalCategories(selectedAccountId, {
                    level: Number(internalCategoryLevel),
                    limit: 5000,
                });
                if (!rows.length) {
                    setStatus("Нет категорий выбранного уровня с остатками.");
                    return;
                }
                await Promise.all(rows.map((item) => upsertPromCategoryMapping(selectedAccountId, {
                    ...basePayload,
                    internal_category_code: item.internal_category_code,
                    internal_category_level: item.internal_category_level ?? Number(internalCategoryLevel),
                })));
                setStatus(`Category mapping сохранён для всех категорий уровня ${internalCategoryLevel}: ${rows.length}.`);
            } else {
                await upsertPromCategoryMapping(selectedAccountId, basePayload);
                setStatus("Category mapping сохранён.");
            }
            await loadAccountDetails();
            setCategoryDraft({
                internal_category_code: "",
                internal_category_level: internalCategoryLevel,
                platform_group_id: PROM_ROOT_GROUP.id,
                platform_group_name_ua: PROM_ROOT_GROUP.name,
                platform_category_id: "",
                platform_category_url: "",
                is_active: true,
            });
            setInternalCategorySelection("");
            setInternalCategorySearch("");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сохранить category mapping."));
        }
    };

    const refreshPromCategoryLists = async () => {
        if (!selectedAccountId) {
            return;
        }
        const [categoryRows, quickRows] = await Promise.all([
            getPromPlatformCategories(selectedAccountId, {
                search: platformCategorySearch || undefined,
                limit: 100,
            }),
            getPromPlatformCategories(selectedAccountId, { quick: true, limit: 100 }),
        ]);
        setPlatformCategories(categoryRows || []);
        setPlatformQuickCategories(quickRows || []);
    };

    const loadInternalCategories = async (levelOverride = internalCategoryLevel, searchOverride = internalCategorySearch) => {
        if (!selectedAccountId) {
            return;
        }
        try {
            const result = await getPromInternalCategories(selectedAccountId, {
                search: searchOverride || undefined,
                level: Number(levelOverride),
                limit: 1000,
            });
            setInternalCategories(result || []);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось загрузить наши категории."));
        }
    };

    const loadPlatformCategories = useCallback(async (searchOverride = platformCategorySearch) => {
        if (!selectedAccountId) {
            return;
        }
        try {
            setStatus("Поиск категории Prom...");
            const result = await getPromPlatformCategories(selectedAccountId, {
                search: searchOverride || undefined,
                limit: 100,
            });
            setPlatformCategories(result || []);
            setPlatformCategoryDropdownOpen(true);
            setStatus(`Категорий Prom найдено: ${(result || []).length}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось найти категории Prom."));
        }
    }, [platformCategorySearch, selectedAccountId]);

    useEffect(() => {
        if (!selectedAccountId) {
            return undefined;
        }
        const query = String(platformCategorySearch || "").trim();
        if (query.length < 2) {
            return undefined;
        }
        const timer = setTimeout(() => {
            loadPlatformCategories(query);
        }, 350);
        return () => clearTimeout(timer);
    }, [loadPlatformCategories, platformCategorySearch, selectedAccountId]);

    const runPromPlatformCategoriesSync = async () => {
        if (!selectedAccountId) {
            return;
        }
        try {
            setRunningAction("prom_categories_sync");
            setStatus("Синхронизация справочника категорий Prom...");
            const result = await syncPromPlatformCategories(selectedAccountId);
            await refreshPromCategoryLists();
            setStatus(`Категории Prom синхронизированы: ${result.imported}, дублей ${result.duplicates}, пропущено ${result.skipped}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Prom не отдал XLS серверу. Загрузите файл категорий вручную."));
        } finally {
            setRunningAction("");
        }
    };

    const uploadPlatformCategories = async () => {
        if (!selectedAccountId || !platformCategoryFile) {
            setStatus("Выберите XLS-файл категорий Prom.");
            return;
        }
        try {
            setRunningAction("prom_categories_upload");
            setStatus("Загрузка справочника категорий Prom...");
            const result = await uploadPromPlatformCategories(selectedAccountId, platformCategoryFile);
            await refreshPromCategoryLists();
            setStatus(`Категории Prom загружены: ${result.imported}, дублей ${result.duplicates}, пропущено ${result.skipped}.`);
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось загрузить справочник категорий Prom."));
        } finally {
            setRunningAction("");
        }
    };

    const applyPromGroupToCategoryDraft = (groupId) => {
        const group = [PROM_ROOT_GROUP].find((item) => String(item.id) === String(groupId));
        setCategoryDraft((current) => ({
            ...current,
            platform_group_id: group?.id || "",
            platform_group_name_ua: group?.name || "",
        }));
    };

    const applyInternalCategoryToDraft = (item) => {
        setCategoryDraft((current) => ({
            ...current,
            internal_category_code: item.internal_category_code || "",
            internal_category_level: item.internal_category_level ?? "",
            platform_group_id: item.platform_group_id || current.platform_group_id || PROM_ROOT_GROUP.id,
            platform_group_name_ua: item.platform_group_name_ua || current.platform_group_name_ua || PROM_ROOT_GROUP.name,
            platform_category_id: item.platform_category_id || current.platform_category_id,
            platform_category_url: item.platform_category_url || current.platform_category_url,
            is_active: true,
        }));
        setInternalCategorySelection(item.internal_category_code || "");
        setInternalCategorySearch(item.internal_category_code ? `${item.internal_category_code} · ${item.internal_category_name}` : "");
        setInternalCategoryDropdownOpen(false);
    };

    const applyPlatformCategoryToDraft = (item) => {
        setCategoryDraft((current) => ({
            ...current,
            platform_group_id: current.platform_group_id || PROM_ROOT_GROUP.id,
            platform_group_name_ua: current.platform_group_name_ua || PROM_ROOT_GROUP.name,
            platform_category_id: item.category_id || "",
            platform_category_url: item.category_url || "",
        }));
        setPlatformCategorySearch(item.category_name_ua || item.terminal_category_name_ua || "");
        setPlatformCategoryDropdownOpen(false);
    };

    const applyQuickPromCategoryToDraft = (item) => {
        applyPlatformCategoryToDraft(item);
    };

    const markSelectedPromCategoryQuick = async () => {
        if (!selectedAccountId || !categoryDraft.platform_category_id) {
            setStatus("Выберите категорию Prom.");
            return;
        }
        try {
            const saved = await markPromPlatformCategoryQuick(selectedAccountId, categoryDraft.platform_category_id);
            setPlatformQuickCategories((current) => {
                const filtered = current.filter((item) => String(item.category_id) !== String(saved.category_id));
                return [saved, ...filtered];
            });
            setPlatformCategories((current) => current.map((item) => (
                String(item.category_id) === String(saved.category_id) ? saved : item
            )));
            setStatus("Категория Prom добавлена в быстрые.");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось сделать категорию быстрой."));
        }
    };

    const removeQuickPromCategory = async (item) => {
        if (!selectedAccountId) {
            return;
        }
        try {
            const saved = await unmarkPromPlatformCategoryQuick(selectedAccountId, item.category_id);
            setPlatformQuickCategories((current) => current.filter((category) => String(category.category_id) !== String(saved.category_id)));
            setPlatformCategories((current) => current.map((category) => (
                String(category.category_id) === String(saved.category_id) ? saved : category
            )));
            setStatus("Категория удалена из быстрых.");
        } catch (error) {
            setStatus(formatApiError(error, "Не удалось удалить категорию из быстрых."));
        }
    };

    const editExistingMapping = (mapping) => {
        setCategoryDraft({
            internal_category_code: mapping.internal_category_code || "",
            internal_category_level: mapping.internal_category_level ?? "",
            platform_group_id: mapping.platform_group_id || PROM_ROOT_GROUP.id,
            platform_group_name_ua: mapping.platform_group_name_ua || PROM_ROOT_GROUP.name,
            platform_category_id: mapping.platform_category_id || "",
            platform_category_url: mapping.platform_category_url || "",
            is_active: Boolean(mapping.is_active),
        });
        setInternalCategoryLevel(String(mapping.internal_category_level || 2));
        setInternalCategorySelection(mapping.internal_category_code || "");
        setInternalCategorySearch(mapping.internal_category_code || "");
        setStatus("Mapping загружен в форму. Измените поля и нажмите “Сохранить mapping”.");
    };

    const applyInternalCategorySelection = (value) => {
        setInternalCategorySelection(value);
        if (value === "__all__") {
            setCategoryDraft((current) => ({
                ...current,
                internal_category_code: `__all_level_${internalCategoryLevel}__`,
                internal_category_level: Number(internalCategoryLevel),
            }));
            setInternalCategorySearch(`Все категории уровня ${internalCategoryLevel}`);
            setInternalCategoryDropdownOpen(false);
            return;
        }
        const item = internalCategories.find((category) => category.internal_category_code === value);
        if (item) {
            applyInternalCategoryToDraft(item);
            return;
        }
        setCategoryDraft((current) => ({
            ...current,
            internal_category_code: "",
            internal_category_level: Number(internalCategoryLevel),
        }));
        setInternalCategorySearch("");
    };

    const normalizeCategorySearch = (value) => String(value || "").toLowerCase().trim();
    const filteredInternalCategories = useMemo(() => {
        const query = normalizeCategorySearch(internalCategorySearch);
        if (!query || internalCategorySelection === "__all__") {
            return internalCategories.slice(0, 80);
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return internalCategories.filter((item) => {
            const text = normalizeCategorySearch([
                item.internal_category_code,
                item.internal_category_name,
                item.products_count,
                item.mapped ? "уже связано mapped" : "",
            ].join(" "));
            return terms.every((term) => text.includes(term));
        }).slice(0, 80);
    }, [internalCategories, internalCategorySearch, internalCategorySelection]);

    const selectedInternalCategory = internalCategories.find(
        (item) => item.internal_category_code === categoryDraft.internal_category_code,
    );

    const availablePromGroups = [
        PROM_ROOT_GROUP,
    ];

    const allVisiblePromCategories = [
        ...platformQuickCategories,
        ...platformCategories,
    ];
    const selectedPromCategory = allVisiblePromCategories.find(
        (item) => String(item.category_id) === String(categoryDraft.platform_category_id),
    );
    const mappedActiveCount = categoryMappings.filter((mapping) => mapping.is_active).length;
    const promCategoryNameById = (categoryId) => (
        allVisiblePromCategories.find((item) => String(item.category_id) === String(categoryId))?.category_name_ua || ""
    );

    return (
        <div className="integrations-page" style={{ display: "grid", gap: "16px" }}>
            <PromTopBar
                activeKey={promSection}
                accountId={selectedAccountId}
                localKeys={["general", "senders", "export", "pricing", "orders"]}
                onLocalSelect={setPromSection}
            />
            {status ? <div className="ip-status">{status}</div> : null}

            <section className="ip-card" style={{ display: promSection === "general" ? "block" : "none" }}>
                <div className="ip-card-body">
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
                        <div>
                            <h2 className="ip-title">Prom.ua seller account</h2>
                            <p className="ip-lead">Система готовит YML feed с UA-контентом, ценами и остатками. Prom забирает данные по публичному URL feed.</p>
                        </div>
                        <div className="ip-acts">
                            <button type="button" className="ip-btn ip-btn-primary" onClick={saveAccount}>Сохранить account</button>
                        </div>
                    </div>
                    <div className="ip-grid4">
                        <label className="ip-fld"><span>Выбранный account</span>
                            <select
                                className="ip-inp"
                                value={selectedAccountId || ""}
                                onChange={(event) => setSelectedAccountId(event.target.value ? Number(event.target.value) : null)}
                            >
                                <option value="">Новый account</option>
                                {accounts.map((item) => (
                                    <option key={item.id} value={item.id}>{item.account_name} ({item.account_code})</option>
                                ))}
                            </select>
                        </label>
                        <label className="ip-fld"><span>Account code</span>
                            <input className="ip-inp" value={draft.account_code ?? ""} onChange={(e) => setDraft({ ...draft, account_code: e.target.value })} />
                        </label>
                        <label className="ip-fld"><span>Account name</span>
                            <input className="ip-inp" value={draft.account_name ?? ""} onChange={(e) => setDraft({ ...draft, account_name: e.target.value })} />
                        </label>
                        <label className="ip-fld"><span>Предприятие / юрлицо</span>
                            <select
                                className="ip-inp"
                                value={draft.business_organization_id || ""}
                                onChange={(event) => setDraft({ ...draft, business_organization_id: event.target.value })}
                            >
                                <option value="">Выберите юрлицо</option>
                                {organizations.map((org) => (
                                    <option key={org.id} value={org.id}>
                                        {org.short_name || org.name || `ID ${org.id}`}{org.tax_id ? ` / ${org.tax_id}` : ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="ip-fld"><span>API base URL</span>
                            <input className="ip-inp" value={draft.base_url ?? ""} onChange={(e) => setDraft({ ...draft, base_url: e.target.value })} />
                        </label>
                        <label className="ip-fld"><span>Bearer token</span>
                            <input className="ip-inp" type="password" value={draft.api_token || ""} onChange={(e) => setDraft({ ...draft, api_token: e.target.value })} />
                            <span className="ip-help-text">{draft.api_token_set ? "Токен сохранён. Оставьте пустым, чтобы не менять." : "Bearer token для авторизации в Prom API."}</span>
                        </label>
                        <label className="ip-fld"><span>Макс товаров в feed</span>
                            <input className="ip-inp" type="number" value={draft.feed_product_limit ?? ""} onChange={(e) => setDraft({ ...draft, feed_product_limit: e.target.value })} />
                            <span className="ip-help-text">Жёсткий максимум для ручной и регламентной генерации. Например 1000: в feed попадёт не больше 1000 валидных товаров.</span>
                        </label>
                        <label className="ip-fld"><span>Лимит сканирования Prom API</span>
                            <input className="ip-inp" type="number" value={draft.prom_product_scan_limit ?? ""} onChange={(e) => setDraft({ ...draft, prom_product_scan_limit: e.target.value })} />
                            <span className="ip-help-text">Сколько товаров Prom просматривать при поиске связок. Максимум 10000; feed limit не меняет.</span>
                        </label>
                        <label className="ip-fld"><span>Обновление фида, sec</span>
                            <input className="ip-inp" type="number" value={draft.catalog_schedule_interval_sec ?? ""} onChange={(e) => setDraft({ ...draft, catalog_schedule_interval_sec: e.target.value })} />
                            <span className="ip-help-text">В этом интервале пересобирается весь feed: контент, цены и остатки.</span>
                        </label>
                        <label className="ip-fld"><span>Профиль карточки Prom</span>
                            <select className="ip-inp" value={draft.feed_content_profile || "basic"} onChange={(event) => setDraft({ ...draft, feed_content_profile: event.target.value })}>
                                <option value="basic">Базовый (текущий)</option>
                                <option value="enriched">Расширенная карточка</option>
                            </select>
                            <span className="ip-help-text">Basic сохраняет текущий feed. Расширенный добавляет до 10 фото, характеристики, города, vendor и габариты.</span>
                        </label>
                        <label className="ip-fld ip-col-span-2"><span>API цены/остатки, sec</span>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                <input
                                    className="ip-inp"
                                    type="number"
                                    style={{ maxWidth: "140px" }}
                                    value={draft.stock_price_schedule_interval_sec ?? ""}
                                    onChange={(event) => setDraft({ ...draft, stock_price_schedule_interval_sec: event.target.value })}
                                />
                                {PROM_STOCK_PRICE_INTERVAL_PRESETS.map((preset) => (
                                    <button
                                        key={preset.value}
                                        type="button"
                                        className={Number(draft.stock_price_schedule_interval_sec) === preset.value ? "ip-btn ip-btn-sm ip-btn-primary" : "ip-btn ip-btn-sm"}
                                        onClick={() => setDraft({ ...draft, stock_price_schedule_interval_sec: preset.value })}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                            <span className="ip-help-text">Как часто через Prom API обновлять price, quantity_in_stock, presence. Контент не обновляется.</span>
                        </label>
                    </div>
                    <div className="ip-check-row">
                        <label className={`ip-check${draft.is_active ? " ck" : ""}`}>
                            <input className="ip-box" type="checkbox" checked={Boolean(draft.is_active)} onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })} />
                            Активен
                        </label>
                    </div>
                    <div className="ip-save-bar">
                        <button
                            type="button"
                            className="ip-btn"
                            onClick={runStockPriceSync}
                            disabled={!selectedAccountId || runningAction === "stock_price_sync"}
                        >
                            {runningAction === "stock_price_sync" ? "Обновляется..." : "Обновить цены/остатки сейчас"}
                        </button>
                        <span className="ip-note">Запускает обновление price, quantity_in_stock и presence без пересборки контента.</span>
                    </div>
                </div>
            </section>

            {selectedAccountId ? (
                <>
                    <div style={{ display: promSection === "senders" ? "block" : "none" }}>
                        <MarketplaceSendersTable
                            platform="prom"
                            accountId={selectedAccountId}
                            active={promSection === "senders"}
                            defaultProfit={promPricingMode.defaultProfit}
                            onSaved={() => loadAccountDetails()}
                        />
                    </div>

                    <section style={{ ...panelStyle, display: promSection === "export" ? "block" : "none" }}>
                        <div style={{ ...panelBodyStyle, display: "grid", gap: "14px" }}>
                            <h2 style={sectionTitleStyle}>Выгрузка feed</h2>
                            <div style={infoBoxStyle}>
                                <strong>URL для Prom:</strong>{" "}
                                {latestFeedUrl ? (
                                    <code style={{ wordBreak: "break-all" }}>{latestFeedUrl}</code>
                                ) : (
                                    "появится после первой генерации feed"
                                )}
                                <div style={{ marginTop: "8px" }}>
                                    <button type="button" style={buttonStyle} onClick={copyLatestFeedUrl}>Скопировать URL</button>
                                </div>
                            </div>
                            <div style={gridFourStyle}>
                                <label style={{ ...fieldContainerStyle, maxWidth: "360px" }}>
                                    <span style={fieldLabelTextStyle}>Excel scope SKU</span>
                                    <input
                                        type="file"
                                        accept=".xlsx,.xlsm,.xltx,.xltm"
                                        onChange={(event) => setScopeFile(event.target.files?.[0] || null)}
                                        style={{ ...inputStyle, paddingTop: "10px" }}
                                    />
                                    <span style={fieldHintStyle}>Порядок берётся из Excel сверху вниз. Если top-файл отсортирован по убыванию, feed сохранит этот порядок и возьмёт не больше лимита account.</span>
                                </label>
                            </div>
                            <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                                <button type="button" style={buttonStyle} onClick={uploadScope} disabled={Boolean(runningAction)}>
                                    {runningAction === "scope_upload" ? "Загрузка..." : "Загрузить scope"}
                                </button>
                                <button type="button" style={buttonStyle} onClick={clearScope} disabled={Boolean(runningAction)}>
                                    {runningAction === "scope_clear" ? "Очистка..." : "Очистить scope (все товары)"}
                                </button>
                                <button type="button" style={primaryButtonStyle} onClick={() => runFeed(false)} disabled={Boolean(runningAction)}>
                                    {runningAction === "feed_generate" ? "Генерация..." : "Сгенерировать feed"}
                                </button>
                                <button type="button" style={buttonStyle} onClick={() => runFeed(true)} disabled={Boolean(runningAction)}>
                                    Dry-run расширенного feed
                                </button>
                            </div>
                            <div style={{ ...infoBoxStyle, backgroundColor: "#ffffff", display: "grid", gap: "8px" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                                    <div><strong>Города наличия по поставщику</strong><div style={fieldHintStyle}>В offer попадает до трёх городов выбранного отправителя. Для D14, D15 и D16 уже указан Киев.</div></div>
                                    <button type="button" style={buttonStyle} onClick={() => { setShowSupplierRegions((value) => !value); setEditingSupplierRegions(""); }}>
                                        {showSupplierRegions ? "Скрыть настройку городов" : "Настроить города"}
                                    </button>
                                </div>
                                <div style={{ display: "grid", gap: "6px" }}>
                                    {suppliers.map((supplier) => {
                                        const savedRows = supplierRegions.filter((item) => item.supplier_code === supplier.supplier_code);
                                        const cityNames = savedRows.map((item) => item.region.name_ua).join(", ");
                                        return <div key={`region-summary-${supplier.supplier_code}`} style={{ display: "grid", gridTemplateColumns: "minmax(260px, 1fr) auto", gap: "8px", alignItems: "center", fontSize: "13px" }}>
                                            <span><strong>{supplier.supplier_code}</strong>{supplier.supplier_name ? ` · ${supplier.supplier_name}` : ""} — <span style={{ color: savedRows.length ? "#166534" : "#64748b", fontWeight: 700 }}>сохранено {savedRows.length}/3</span>{cityNames ? `: ${cityNames}` : ""}</span>
                                            {showSupplierRegions ? <button type="button" style={{ ...buttonStyle, padding: "5px 9px" }} onClick={() => setEditingSupplierRegions((current) => current === supplier.supplier_code ? "" : supplier.supplier_code)}>{editingSupplierRegions === supplier.supplier_code ? "Закрыть" : "Изменить"}</button> : null}
                                        </div>;
                                    })}
                                </div>
                                {showSupplierRegions ? <div style={{ display: "grid", gap: "8px", borderTop: "1px solid #e2e8f0", paddingTop: "10px" }}>
                                    <input style={inputStyle} value={regionSearch} onChange={(event) => setRegionSearch(event.target.value)} placeholder="Поиск города или ID Prom" />
                                    {suppliers.filter((supplier) => supplier.supplier_code === editingSupplierRegions).map((supplier) => {
                                        const saved = supplierRegions.filter((item) => item.supplier_code === supplier.supplier_code).map((item) => item.region.id);
                                        const selected = regionDrafts[supplier.supplier_code] ?? saved;
                                        return <div key={`regions-editor-${supplier.supplier_code}`} style={{ display: "grid", gap: "8px" }}>
                                            <strong>{supplier.supplier_code}{supplier.supplier_name ? ` · ${supplier.supplier_name}` : ""}</strong>
                                            <select multiple size="6" style={{ ...inputStyle, minHeight: "132px" }} value={selected.map(String)} onChange={(event) => setRegionDrafts((current) => ({ ...current, [supplier.supplier_code]: Array.from(event.target.selectedOptions).map((option) => Number(option.value)).slice(0, 3) }))}>
                                                {promRegions.map((region) => <option key={region.id} value={region.id}>{region.name_ua} · {region.prom_region_id}</option>)}
                                            </select>
                                            <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center", flexWrap: "wrap" }}><span style={fieldHintStyle}>Выбрано для сохранения: {selected.length}/3.</span><button type="button" style={buttonStyle} onClick={() => saveSupplierRegions(supplier.supplier_code)}>Сохранить</button></div>
                                        </div>;
                                    })}
                                    {!editingSupplierRegions ? <span style={fieldHintStyle}>Выберите «Изменить» у нужного отправителя — большой список городов откроется только для него.</span> : null}
                                </div> : null}
                            </div>
                            {latestFeedJob ? (
                                <div style={{ ...warningBoxStyle, backgroundColor: "#ffffff" }}>
                                    <strong>Последняя генерация feed:</strong>{" "}
                                    {statusLabel(latestFeedJob.status)} · {formatJobTime(latestFeedJob)} · job {latestFeedJob.id}
                                    <div style={{ marginTop: "6px" }}>
                                        В feed попало: <strong>{latestFeedJob.processed}</strong>, skipped: <strong>{latestFeedJob.skipped}</strong>, errors: <strong>{latestFeedJob.errors}</strong>.
                                        {latestFeedJob.feed_file_path ? " Файл создан." : " Файл не создан."}
                                    </div>
                                    {latestFeedJob.skip_reasons?.length ? (
                                        <div style={{ color: "#64748b", fontSize: "12px", marginTop: "6px", display: "grid", gap: "2px" }}>
                                            <strong>Основные причины skipped:</strong>
                                            {latestFeedJob.skip_reasons.slice(0, 3).map((item) => (
                                                <span key={`${latestFeedJob.id}-${item.reason}`}>{item.count}: {item.reason}</span>
                                            ))}
                                        </div>
                                    ) : null}
                                    {latestFeedJob.response_payload?.skipped_report_path ? (
                                        <div style={{ marginTop: "8px" }}>
                                            <button
                                                type="button"
                                                style={buttonStyle}
                                                onClick={() => downloadPromSkippedReport(selectedAccountId, latestFeedJob.id, `prom_skipped_report_job_${latestFeedJob.id}.xlsx`)}
                                            >
                                                Скачать skipped-отчёт
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}
                            <div>
                                <button type="button" style={buttonStyle} onClick={() => setShowFeedHistory((value) => !value)}>
                                    {showFeedHistory ? "Скрыть историю генераций" : "Показать историю генераций"}
                                </button>
                            </div>
                            {showFeedHistory ? (
                                <div style={{ overflowX: "auto" }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                        <thead>
                                            <tr>
                                                {["Job", "Дата/время", "Суть", "Status", "В feed", "Skipped", "Errors", "File", "Skipped report"].map((header) => (
                                                    <th key={header} style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>{header}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {feedJobs.map((job) => (
                                                <tr key={job.id}>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.id}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", minWidth: "190px" }}>{formatJobTime(job)}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{jobTypeLabel(job.job_type)}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.status}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.processed}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", minWidth: "240px" }}>
                                                        <strong>{job.skipped}</strong>
                                                    </td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.errors}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", maxWidth: "420px", wordBreak: "break-all" }}>{job.feed_file_path || "нет файла"}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>
                                                        {job.response_payload?.skipped_report_path ? (
                                                            <button
                                                                type="button"
                                                                style={{ ...buttonStyle, padding: "5px 9px" }}
                                                                onClick={() => downloadPromSkippedReport(selectedAccountId, job.id, `prom_skipped_report_job_${job.id}.xlsx`)}
                                                            >
                                                                Скачать
                                                            </button>
                                                        ) : "—"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : null}
                        </div>
                    </section>

                    <section style={{ ...panelStyle, display: promSection === "export" ? "block" : "none" }}>
                        <div style={{ ...panelBodyStyle, display: "grid", gap: "14px" }}>
                            <div>
                                <h2 style={sectionTitleStyle}>API цены/остатки Prom</h2>
                                <p style={mutedTextStyle}>Feed отдаёт контент. Этот контур часто обновляет только price, quantity_in_stock и presence через Prom API.</p>
                            </div>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                                <button
                                    type="button"
                                    style={buttonStyle}
                                    onClick={runProductLinksSync}
                                    disabled={!selectedAccountId || Boolean(runningAction)}
                                >
                                    {runningAction === "product_links_sync" ? "Синхронизация..." : "Синхронизировать связки товаров"}
                                </button>
                                <span style={hintTextStyle}>
                                    Интервал автообновления: {draft.stock_price_schedule_interval_sec || 900} sec. Кнопка обновления цен/остатков находится в настройках account выше.
                                </span>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "10px" }}>
                                <div style={{ ...infoBoxStyle, backgroundColor: "#ffffff" }}>
                                    <strong>Последняя синхронизация связок товаров:</strong>{" "}
                                    {latestLinksJob ? (
                                        <span>
                                            {statusLabel(latestLinksJob.status)} · {formatJobTime(latestLinksJob)} · job {latestLinksJob.id}
                                            <div style={{ marginTop: "6px", color: "#64748b" }}>
                                                В текущем feed: <strong>{latestLinksJob.response_payload?.target_codes || latestLinksJob.total}</strong>,
                                                найдено в Prom: <strong>{latestLinksJob.processed}</strong>,
                                                без связки в Prom: <strong>{latestLinksJob.skipped}</strong>,
                                                errors: <strong>{latestLinksJob.errors}</strong>
                                                {latestLinksJob.response_payload?.prom_products_scanned ? `, проверено товаров в кабинете Prom: ${latestLinksJob.response_payload.prom_products_scanned}` : ""}
                                                {latestLinksJob.response_payload?.scan_limited ? ". Поиск остановлен лимитом проверки." : ""}
                                            </div>
                                        </span>
                                    ) : "ещё не запускалась"}
                                </div>
                                <div style={{ ...infoBoxStyle, backgroundColor: "#ffffff" }}>
                                    <strong>Последнее обновление цен/остатков API:</strong>{" "}
                                    {latestStockPriceJob ? (
                                        <span>
                                            {statusLabel(latestStockPriceJob.status)} · {formatJobTime(latestStockPriceJob)} · job {latestStockPriceJob.id}
                                            <div style={{ marginTop: "6px", color: "#64748b" }}>
                                                Обновлено: <strong>{latestStockPriceJob.processed}</strong>, skipped/без изменений: <strong>{latestStockPriceJob.skipped}</strong>, errors: <strong>{latestStockPriceJob.errors}</strong>
                                            </div>
                                        </span>
                                    ) : "ещё не запускалось"}
                                </div>
                            </div>
                            <div style={{ overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead>
                                        <tr>
                                            {["Job", "Дата/время", "Суть", "Status", "Processed", "Skipped", "Errors"].map((header) => (
                                                <th key={header} style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #e2e8f0" }}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...linkJobs, ...stockPriceJobs]
                                            .sort((left, right) => Number(right.id) - Number(left.id))
                                            .slice(0, 8)
                                            .map((job) => (
                                                <tr key={job.id}>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.id}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9", minWidth: "190px" }}>{formatJobTime(job)}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{jobTypeLabel(job.job_type)}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.status}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.processed}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.skipped}</td>
                                                    <td style={{ padding: "8px", borderBottom: "1px solid #f1f5f9" }}>{job.errors}</td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                    <div style={{ display: promSection === "pricing" ? "block" : "none" }}>
                        <MarketplacePricingTab
                            platform="prom"
                            accountId={selectedAccountId}
                            active={promSection === "pricing"}
                            onModeChange={promPricingMode.reload}
                        />
                    </div>

                    <div style={{ display: promSection === "orders" ? "block" : "none" }}><MarketplaceOrdersPanel account={selectedAccount} onAccountUpdated={loadAccounts} /></div>
                </>
            ) : null}
        </div>
    );
};

const integrationTabs = [
    ["nova-poshta", "Новая почта", "/integrations/nova-poshta"],
    ["tabletki", "Tabletki.ua", "/integrations/tabletki"],
    ["checkbox", "Checkbox", "/integrations/checkbox"],
    ["prom", "Prom.ua", "/integrations/prom"],
    ["rozetka", "Rozetka", "/integrations/rozetka"],
    ["kasta", "Kasta", "/integrations/kasta"],
    ["turbosms", "TurboSMS", "/integrations/turbosms"],
];

const IntegrationsPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    if (location.pathname === "/integrations") {
        return <Navigate to="/integrations/nova-poshta" replace />;
    }

    // Страницы маппинга Rozetka и Prom -- часть соответствующего раздела,
    // просто с другим телом внутри общей обвязки "Интеграции" (заголовок +
    // переключатель Новая почта/Tabletki.ua/.../Rozetka), а не отдельный
    // экран без неё.
    const isRozetkaSubPage = location.pathname === "/integrations/rozetka/mappings" || location.pathname === "/integrations/rozetka/attributes";
    const isPromSubPage = location.pathname === "/integrations/prom/mappings" || location.pathname === "/integrations/prom/attributes";
    const activeIntegration = isRozetkaSubPage ? "rozetka" : isPromSubPage ? "prom" : location.pathname.split("/").pop();

    if (!isRozetkaSubPage && !isPromSubPage && !integrationTabs.some(([, , path]) => path === location.pathname)) {
        return <Navigate to="/integrations/nova-poshta" replace />;
    }

    return (
        <div className="integrations-page" style={pageStyle}>
            <section className="ip-card">
                <div className="ip-card-body">
                    <div className="ip-head">
                        <div>
                        <h1 className="ip-title" style={{ fontSize: "22px" }}>Интеграции</h1>
                        <p className="ip-lead">
                            {activeIntegration === "tabletki"
                                ? "Tabletki.ua: глобальные endpoints и служебные коды обмена."
                                : activeIntegration === "checkbox"
                                    ? "Checkbox: глобальные параметры фискализации и переход с env на DB-first настройки."
                                    : activeIntegration === "prom"
                                        ? "Prom.ua: генерация товарного feed с контентом, ценами, остатками и Excel-ограничением SKU."
                                        : activeIntegration === "rozetka"
                                            ? "Rozetka: авторизация, справочники, маппинг категорий/брендов/характеристик, генерация фида и синхронизация остатков/цен через API."
                                            : activeIntegration === "kasta"
                                                ? "Kasta: отдельный кабинет продавца, справочники API и ручное сопоставление XML через HUB."
                                            : activeIntegration === "turbosms"
                                                ? "TurboSMS: транзакционные SMS по правилам обработки заказов."
                                            : "Новая почта: кабинеты юрлиц, точки отправки, fulfillment и технические параметры WMS."}
                        </p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="ip-toplevel-tabs">
                {integrationTabs.map(([key, label, path]) => (
                    <button
                        key={key}
                        type="button"
                        className={activeIntegration === key ? "ip-toplevel-tab active" : "ip-toplevel-tab"}
                        onClick={() => navigate(path)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {activeIntegration === "tabletki"
                ? <TabletkiIntegrationPage />
                : activeIntegration === "checkbox"
                    ? <CheckboxIntegrationPage />
                    : activeIntegration === "prom"
                        ? (isPromSubPage
                            ? (location.pathname === "/integrations/prom/attributes" ? <PromAttributesPage /> : <PromMappingsPage />)
                            : <PromIntegrationPage />)
                        : activeIntegration === "rozetka"
                            ? (isRozetkaSubPage ? <RozetkaMappingPage /> : <RozetkaIntegrationPage />)
                            : activeIntegration === "kasta"
                                ? <KastaIntegrationPage />
                            : activeIntegration === "turbosms"
                                ? <TurboSmsIntegrationPage />
                            : <NovaPoshtaIntegrationPage />}
        </div>
    );
};

export default IntegrationsPage;
